import { SETTINGS_VIEW } from '../contexts/UIContext';
import { SCOPE_DEVIANCE_STANDARD } from '../helpers/constant';
import { IApplicationException } from './exception.type';
import { NorthStarTarget, NorthStarTransfoProgress } from './north-star-target.type';
import { PersistentResultDTO } from './persistent-result.type';
import { IRecommendationNext, RecommendationStatus } from './recommendation-next.type';
import { IRoadmap } from './roadmap.type';
import { RoadmapAction } from './roadmapAction.type';
import { IUser } from './user.type';

export interface INode {
  label: string;
  key: string | number;
  children?: INode[];
  fullPath?: string;
  selected?: boolean;
  disabled?: boolean;
  level?: number;
  parent?: INode;
  opened?: boolean;
  category?: boolean;
}

export interface ISection {
  path: string;
  label: string;
  display: boolean;
  component: JSX.Element | undefined;
  isLink?: boolean;
  notification?: number;
  applyFilter?: boolean;
  displayNotification?: boolean;
  childrens?: ISection[];
  isFullScreen?: boolean;
  isNew?: boolean;
}

export interface Paginated<T> {
  items: T[];
  meta: IPagination;
}

// This is a type that represents a paginated list by nestjs-paginate.
export interface PaginatedNext<T> {
  data: T[];
  meta: {
    applicationCount: number;
    hostnameCount: number;
    currentPage: number;
    itemsPerPage: number;
    totalItems: number;
    totalPages: number;
    sortBy: Array<[string, 'ASC' | 'DESC']>;
  };
}

export type GlobalStatus = 'idle' | 'error' | 'maintenance';
export type WorkerStatus = 'idle' | 'active' | 'error';

export interface IStatus {
  global: GlobalStatus;
  worker: WorkerStatus;
  lastRecordDate: string;
  lastApptioRecordMonth: string;
}

export interface IReferenceValue {
  key: number | string;
  name: string;
  resultKey?: string;
  disabled?: boolean;
  desc?: string;
  componentRefType?: string;
}

export const ALLOWED_TRIBES = [
  'GCOO/CFT/RRR',
  'GCOO/RBS/PBI/LUX/MKT',
  'GCOO/RBS/PBI/LUX/PIL',
  'GCOO/ARC/LUX',
  'GCOO/RBS/DCC/DEV/VDF',
  'GCOO/RBS/ASR/CSS/VDF',
  'GBTO/LUX/TSU',
  'GCOO/ISR/GBI',
];
export type ManagedByOptions = 'GBIS-GBSU' | 'GBIS-Non GBSU' | 'Non GBIS';

export type FlagType =
  | 'success'
  | 'warning'
  | 'danger'
  | 'dark'
  | 'secondary'
  | 'light'
  | 'transparent'
  | 'white'
  | 'black'
  | 'primary';

export type ApplicationResultType = 'value' | 'rule' | 'presence' | 'percentage';
export type ApplicationResultValue = 'good' | 'medium' | 'bad' | 'n/a' | 'n/d' | string | boolean | number;

export interface IApplicationClaim extends IBaseEntity {
  justification: string;
  status: string;
  application: IBaseEntity;
}

export interface ResultGroupedByName {
  name: string;
  results: IApplicationFullResult[];
}

export interface ResultGroupedByVersion {
  version: string | undefined;
  groupedResultsByName: ResultGroupedByName[];
}

export interface ResultGroupedByComponentRef {
  componentRef: IApplicationComponentRef | null; // null in case we want to display only type column (obsolescence score case)
  groupedResultsByVersion: ResultGroupedByVersion[];
}

export interface IGroupedComponentFull {
  type: string;
  componentsAggrByType: ResultGroupedByComponentRef[];
}

export interface IGroupedTotalCostComponents {
  type: string;
  componentsAggrByType: {
    componentRef: IApplicationComponentRef;
    groupedResults: IGroupedTotalCostResult[];
  }[];
}

export interface IGroupedTotalCostComponent {
  type: string;
  componentsAggrByType: {
    componentRef: IApplicationComponentRef;
    components: (ITotalCostResults | undefined)[];
  }[];
}

export interface IApplicationTab {
  appName: string;
  appId: number;
}

export interface IBaseEntity {
  id: number;
  createdDate: string;
  lastUpdate: string;
  createdBy: string;
  lastUpdateBy: string;
}

export interface IPagination {
  totalItems: number;
  itemCount: number;
  itemsPerPage: number;
  totalPages: number;
  currentPage: number;
}

export interface IApplicationParentRef extends IBaseEntity {
  scopesRef: IApplicationScopeRef[];
  name: string;
  key: string;
  comment: string;
}

export interface IApplicationScopeRef extends IBaseEntity {
  componentsRef: IApplicationComponentRef[];
  key: string;
  name: string;
  comment: string;
  type: string | null;
}

export interface IApplicationRef extends IBaseEntity {
  comment: string;
  key: string;
  name: string;
}

export interface IApplicationConfidence extends IBaseEntity {
  value: number;
  comment: string;
}

export interface IApplicationSource extends IBaseEntity {
  name: string;
  link: string | null;
  comment: string | null;
}

export enum VersionRefStatus {
  DEPRECATED = 'deprecated',
  TO_BE_DEPRECATED = 'to-be-deprecated',
  UP_TO_DATE = 'up-to-date',
}

export enum ChartMode {
  FAVORITE = 'Favorites',
  EXCEPTION = 'Exceptions',
  RECOMMENDATION = 'Recommendations',
  ROADMAP = 'Roadmaps',
  APPLICATION = 'Applications',
  CERTIFICATION = 'Certifications',
  ERRORS = 'Errors',
}
export enum TypeMode {
  ACTUAL = 'Actual',
  PREV_YEAR = 'Prev year',
  FORECAST = 'Forecast',
}

export interface IApplicationVersionRef extends IBaseEntity {
  version: string;
  versionAlias: string | null;
  status: VersionRefStatus;
  description: string;
  advisedVersion: string | null;
  parentRef: IApplicationParentRef;
  parents: IApplicationParent[];
  scopeRef: IApplicationScopeRef;
  scopes: IApplicationScope[];
  componentRef: IApplicationComponentRef;
  components: IApplicationComponent[];
}
export interface IApplicationComponent extends IBaseEntity {
  results: IApplicationResult[];
  componentRef: IApplicationComponentRef;
  versionRef: IApplicationVersionRef | undefined;
  recommendations: IRecommendationNext[];
  scope: IApplicationScope | undefined;
}

export type ComponentRefVersionStatus = 'deprecated' | 'to_be_deprecated' | 'up_to_date';

export interface IApplicationParent extends IBaseEntity {
  parentRef: IApplicationRef;
  scopes: IApplicationScope[];
}

export interface IApplicationRecord extends IBaseEntity {
  name: string;
  type: string;
  parents: IApplicationParent[];
  result: IApplicationResult;
  application: IApplication;
  applicationId: number;
}

export interface IApplication extends IBaseEntity {
  iAppliName: IApplicationResult;
  iAppliID: IApplicationResult;
  description: IApplicationResult | null;
  records: IApplicationRecord[];
  exceptions: IApplicationException[];
  roadmaps: IRoadmap[];
  tags: ITag[];
  users: IUser[];
  globalState: IApplicationResult;
}

export interface IApplicationPaginated extends IBaseEntity {
  iAppliNameId: number;
  iAppliIdid: number;
  globalStateId: number;
  descriptionId: number;
}
export interface IResultPaginated {
  application: IApplicationPaginated;
  results: IApplicationResult[];
}
export interface IApplicationsResultPaginated {
  items: IResultPaginated[];
  totalItems: number;
  currentPage: number;
  totalPages: number;
}

export interface IFavorite extends IBaseEntity {
  application: IApplication;
  user: IUser;
}
export interface IApplicationsPayload {
  items: IApplication[];
  meta: IPagination;
  links: {
    first: string;
    previous: string;
    next: string;
    last: string;
  };
}

export interface IBaseEntity {
  id: number;
  createdDate: string;
  lastUpdate: string;
  createdBy: string;
  lastUpdateBy: string;
}

export interface IPagination {
  totalItems: number;
  itemCount: number;
  itemsPerPage: number;
  totalPages: number;
  currentPage: number;
}

export interface IApplicationParentRef extends IBaseEntity {
  scopesRef: IApplicationScopeRef[];
  name: string;
  key: string;
  comment: string;
}

export interface IApplicationScopeRef extends IBaseEntity {
  componentsRef: IApplicationComponentRef[];
  key: string;
  name: string;
  comment: string;
  type: string | null;
  resultKeys: string[];
}

export type ProfileType = 'Application manager' | 'CIO' | 'RC/Tribe manager' | 'Architect' | 'Other';

export interface IApplicationRef extends IBaseEntity {
  comment: string;
  key: string;
  name: string;
}

export interface IApplicationConfidence extends IBaseEntity {
  value: number;
  comment: string;
}

export interface IApplicationSource extends IBaseEntity {
  name: string;
  link: string | null;
  comment: string | null;
}

export interface IApplicationResult extends IBaseEntity {
  comment: string;
  isMainResult: boolean;
  key: string;
  type: string;
  value: string;
  name: string | null;
  confidence: IApplicationConfidence;
  sources: IApplicationSource[];
  tbs?: ITbsResult;
  chasingId: number | null;
  component?: IApplicationComponent;
  scope?: IApplicationScope;
}
export interface IInfrastructureComponent {
  type: string;
  components: { key: ResultKey; name: string; versions: Array<string> | null }[];
}

export interface IRemediationComponent {
  scopeKey: ResultKey;
  scopeName: string;
  components: { key: ResultKey; name: string; versions: Array<string> | null }[];
}

export interface IApplicationComponentRef extends IBaseEntity {
  type: string;
  name: string;
  key: string;
  comment: string;
  resultKeys: string[];
  scopeRefId: number;
}

export interface IApplicationScope extends IBaseEntity {
  results: IApplicationResult[];
  scopeRef: IApplicationRef;
  components: IApplicationComponent[];
  parent: IApplicationParent;
}

export interface IApplicationParent extends IBaseEntity {
  parentRef: IApplicationRef;
  scopes: IApplicationScope[];
  record: IApplicationRecord;
}

export interface IApplicationRecord extends IBaseEntity {
  name: string;
  type: string;
  parents: IApplicationParent[];
  result: IApplicationResult;
}

export interface IFavorite extends IBaseEntity {
  user: IUser;
}
export interface IApplicationsPayload {
  items: IApplication[];
  meta: IPagination;
  links: {
    first: string;
    previous: string;
    next: string;
    last: string;
  };
}

export interface ITag extends IBaseEntity {
  tagId: number;
  name: string;
  description: string;
  application: IApplication;
}

export interface IParentRefCreate {
  key: string;
  name: string;
  comment?: string;
}

export interface IComponentRefCreate {
  key: string;
  name: string;
  type?: string;
  comment?: string;
  scopeRefID: number;
}

export interface IScopeRefCreate {
  key: string;
  name: string;
  type?: string;
  comment?: string;
  parentRefID: number;
}

export type DASHBOARD_VIEW =
  | 'global_view'
  | 'certification_view'
  | 'infrastructure_view'
  | 'tco_view'
  | 'health_view'
  | 'sgmarkets_view'
  | ResultKey;

export type ApplicationScope =
  | 'global_taxon'
  | 'global_sgmarkets'
  | 'other_obsolescence'
  | 'other_health'
  | 'other_users'
  | 'other_middlewares'
  | 'other_languages'
  | 'digital-capability_web'
  | 'digital-capability_api'
  | 'digital-capability_security'
  | 'digital-capability_ci-cd'
  | 'digital-capability_monitoring'
  | 'tco_cost';

export type ApplicationComponent =
  | 'global_taxon_criticality'
  | 'global_taxon_rto'
  | 'global_taxon_rpo'
  | 'global_taxon_tribe'
  | 'global_taxon_trigram'
  | 'global_taxon_owned-by'
  | 'global_taxon_country'
  | 'global_taxon_application-type'
  | 'global_sgmarkets_sgm-service'
  | 'other_obsolescence_mssql'
  | 'other_obsolescence_oracle'
  | 'other_obsolescence_physical-server'
  | 'other_obsolescence_postgresql'
  | 'other_middlewares_web-middleware'
  | 'other_middlewares_messaging-middleware'
  | 'other_middlewares_tibco-rdv-middleware'
  | 'other_middlewares_autosys-middleware'
  | 'other_languages_jvm-language'
  | 'other_languages_dot-net-language'
  | 'other_languages_c-cpp-language'
  | 'other_languages_python-language'
  | 'other_languages_datascience-language'
  | 'other_languages_web-language'
  | 'digital-capability_web_sgwt'
  | 'digital-capability_api_sgm-api'
  | 'digital-capability_security_sg-connect'
  | 'digital-capability_security_sg-iam'
  | 'digital-capability_ci-cd_github'
  | 'digital-capability_ci-cd_jenkins'
  | 'digital-capability_ci-cd_teamcity'
  | 'digital-capability_ci-cd_nexus'
  | 'digital-capability_ci-cd_artifactory-cdp'
  | 'digital-capability_ci-cd_artifactory-dsp'
  | 'digital-capability_ci-cd_sonar'
  | 'digital-capability_ci-cd_xldeploy'
  | 'digital-capability_monitoring_alerting-alerting'
  | 'digital-capability_monitoring_alerting-multi-channel-alerting'
  | 'digital-capability_monitoring_api-api-monitoring'
  | 'digital-capability_monitoring_app-apm'
  | 'digital-capability_monitoring_app-elasticsearch'
  | 'digital-capability_monitoring_app-elastic-agent-aas'
  | 'digital-capability_monitoring_app-grafana-aas'
  | 'digital-capability_monitoring_app-heartbeat-aas'
  | 'digital-capability_monitoring_app-logstash'
  | 'digital-capability_monitoring_app-metricbeat-aas'
  | 'digital-capability_monitoring_app-open-telemetry-aas'
  | 'digital-capability_monitoring_security-secure-log-platform'
  | 'digital-capability_monitoring_security-vault'
  | 'digital-capability_monitoring_web-web-monitoring'
  | 'tco_cost_gts'
  | 'tco_cost_azure'
  | 'tco_cost_workforce'
  | 'tco_cost_non-workforce'
  | 'tco_cost_software';

export interface ITbsResult {
  [key: string]: { [key: string]: number };
}

export const PARENT_RESULT_KEYS = ['cost', 'cost-prev-year', 'cost-forecast'] as const;

export const SCOPE_RESULT_KEYS = [
  'pops-users',
  'digital-capability-api',
  'digital-capability-security',
  'digital-capability-web',
  'sgmarkets',
  'obsolescence',
  'tbs',
  'finops',
  'digital-capability-monitoring',
  'health',
  'health-changes',
  'max-users',
  'digital-capability-ci-cd',
  'gbsu-cost',
  'gbsu-cost-forecast',
  'gbsu-cost-prev-year',
  'vendor-cost',
  'vendor-cost-forecast',
  'vendor-cost-prev-year',
  'infrastructure-cost',
  'infrastructure-cost-forecast',
  'infrastructure-cost-prev-year',
  //'gbis-standard',
  SCOPE_DEVIANCE_STANDARD,
] as const;

export const COMPONENT_RESULT_KEYS = [
  'digital-capability_api_api-pub',
  'digital-capability_api_average-api-data-quality',
  'digital-capability_api_average-api-functional-quality',
  'digital-capability_api_average-api-qos-quality',
  'digital-capability_api_average-api-security-quality',
  'digital-capability_api_average-api-technical-quality',
  'digital-capability_api_sgm-api',
  'digital-capability_ci-cd_github',
  'digital-capability_ci-cd_jenkins',
  'digital-capability_ci-cd_teamcity',
  'digital-capability_ci-cd_nexus',
  'digital-capability_ci-cd_artifactory-cdp',
  'digital-capability_ci-cd_artifactory-dsp',
  'digital-capability_ci-cd_sonar',
  'digital-capability_ci-cd_xldeploy',
  'digital-capability_monitoring_api-monitoring',
  'digital-capability_monitoring_architecture-type',
  'digital-capability_monitoring_global-monitoring',
  'digital-capability_monitoring_web-monitoring',
  'digital-capability_security_criticality',
  'digital-capability_security_internet-exposition',
  'digital-capability_security_assessment',
  'digital-capability_security_backup',
  'digital-capability_security_database-backup',
  'digital-capability_security_dataset',
  'digital-capability_security_sg-connect',
  'digital-capability_security_sg-iam',
  'digital-capability_security_tsa',
  'digital-capability_security_nist',
  'digital-capability_web_sg-bootstrap',
  'digital-capability_web_sgwt-widgets',
  'digital-capability_web_sgwt-widgets-account-center',
  'digital-capability_web_sgwt-widgets-help-center',
  'digital-capability_web_sgwt-widgets-mini-footer',
  'digital-capability_web_sgwt-widgets-splash-screen',
  'global_sgmarkets_sgm-service',
  'global_taxon_amer-managed',
  'global_taxon_application-type',
  'global_taxon_iappliname',
  'global_taxon_kearid',
  'global_taxon_iappliid',
  'global_taxon_application-manager-email',
  'global_taxon_irt',
  'global_taxon_bdr',
  'global_taxon_languages',
  'global_taxon_application-owner-email',
  'global_taxon_architecture-type',
  'global_taxon_country',
  'global_taxon_criticality',
  'global_taxon_rto',
  'global_taxon_rpo',
  'global_taxon_functional-architecture',
  'global_taxon_managed-by',
  'global_taxon_nova-compatible',
  'global_taxon_status',
  'global_taxon_tribe',
  'global_taxon_trigram',
  'global_taxon_owned-by',
  'global_taxon_ownership-rc',
  'global_taxon_management-rc',
  'global_taxon_application-decom-date',
  'global_taxon_certification-date',
  'global_taxon_deployment-status',
  'global_taxon_deployment-type',
  'global_taxon_game-day-date',
  'global_taxon_game-day-manually-updated',
  'global_taxon_rtc',
  'global_taxon_rpc',
  'tco_finops_orchestrated-container',
  'other_transformation-path_transformation-path',
  'null_null_tbs-enhancement-duplicate',
  'null_null_tbs-project-duplicate',
  'null_null_tbs-support-duplicate',
  'other_gbis-standard_abap',
  'other_gbis-standard_abcpdf-dot-net',
  'other_gbis-standard_action-script',
  'other_gbis-standard_ada',
  'other_gbis-standard_adaptive-server-entreprise-ado-dot-net-data-provider',
  'other_gbis-standard_ado-dot-net',
  'other_gbis-standard_ado-dot-net-entity-framework',
  'other_gbis-standard_ado-dot-net-entity-framework-tools',
  'other_gbis-standard_ags-script',
  'other_gbis-standard_al',
  'other_gbis-standard_alloy',
  'other_gbis-standard_ampl',
  'other_gbis-standard_angel-script',
  'other_gbis-standard_antlr',
  'other_gbis-standard_apache-conf',
  'other_gbis-standard_apache-web-server',
  'other_gbis-standard_apex',
  'other_gbis-standard_api-blueprint',
  'other_gbis-standard_apl',
  'other_gbis-standard_app-dynamics-dot-net-agent',
  'other_gbis-standard_apple-script',
  'other_gbis-standard_arc',
  'other_gbis-standard_asl',
  'other_gbis-standard_asp',
  'other_gbis-standard_asp-dot-net',
  'other_gbis-standard_asp-dot-net-ajax-extensions',
  'other_gbis-standard_asp-dot-net-core-hosting-bundle',
  'other_gbis-standard_asp-dot-net-core-runtime',
  'other_gbis-standard_asp-dot-net-core-runtime-package-store',
  'other_gbis-standard_asp-dot-net-core-runtime-package-store-pre-releases',
  'other_gbis-standard_asp-dot-net-core-shared-framework',
  'other_gbis-standard_asp-dot-net-core-shared-framework-pre-releases',
  'other_gbis-standard_asp-dot-net-frameworkd-and-tools-for-visual-studio-2013',
  'other_gbis-standard_asp-dot-net-lang',
  'other_gbis-standard_asp-dot-net-mvc',
  'other_gbis-standard_asp-dot-net-web-frameworks-and-tools-for-visual-studio-2015',
  'other_gbis-standard_aspect-j',
  'other_gbis-standard_assembly',
  'other_gbis-standard_auto-hoy-key',
  'other_gbis-standard_auto-it',
  'other_gbis-standard_autosys-middleware',
  'other_gbis-standard_awk',
  'other_gbis-standard_basic',
  'other_gbis-standard_batchfile',
  'other_gbis-standard_bicep',
  'other_gbis-standard_bikeshed',
  'other_gbis-standard_blade',
  'other_gbis-standard_blend-for-visual-studio-sdk-for-dot-net',
  'other_gbis-standard_boo',
  'other_gbis-standard_brighscript',
  'other_gbis-standard_brighter-script',
  'other_gbis-standard_businessobjects',
  'other_gbis-standard_business-works',
  'other_gbis-standard_c',
  'other_gbis-standard_cap-n-proto',
  'other_gbis-standard_carto-css',
  'other_gbis-standard_chart-controls-for-microsoft-dot-net-framework',
  'other_gbis-standard_clarion',
  'other_gbis-standard_classic-asp',
  'other_gbis-standard_clean',
  'other_gbis-standard_clojure',
  'other_gbis-standard_cmake',
  'other_gbis-standard_cobol',
  'other_gbis-standard_code-contracts-for-dot-net',
  'other_gbis-standard_coffee-script',
  'other_gbis-standard_cold-fusion',
  'other_gbis-standard_common-lisp',
  'other_gbis-standard_component-art-ui-framework-for-dot-net',
  'other_gbis-standard_connect-express-for-unix',
  'other_gbis-standard_cool',
  'other_gbis-standard_coq',
  'other_gbis-standard_cpp',
  'other_gbis-standard_cruise-control-dot-net',
  'other_gbis-standard_crystal-reports-for-dot-net-framework',
  'other_gbis-standard_crystal-reports-runtime-engine-for-dot-net-framework',
  'other_gbis-standard_c-sharp',
  'other_gbis-standard_csoud-document',
  'other_gbis-standard_csound-score',
  'other_gbis-standard_css',
  'other_gbis-standard_cuda',
  'other_gbis-standard_cython',
  'other_gbis-standard_d',
  'other_gbis-standard_dart',
  'other_gbis-standard_data-provider-for-dot-net',
  'other_gbis-standard_developer-express-dot-net',
  'other_gbis-standard_digital-command-language',
  'other_gbis-standard_dockerfile',
  'other_gbis-standard_documentum',
  'other_gbis-standard_dot-net-compact-framework',
  'other_gbis-standard_dot-net-core-host-pre-releases',
  'other_gbis-standard_dot-net-core-runtime',
  'other_gbis-standard_dot-net-core-runtime-pre-release',
  'other_gbis-standard_dot-net-core-runtime-pre-releases',
  'other_gbis-standard_dot-net-core-tools-visual-studio',
  'other_gbis-standard_dot-net-core-windows-server-hosting',
  'other_gbis-standard_dot-net-framework',
  'other_gbis-standard_dot-net-framework-client-profile',
  'other_gbis-standard_dot-net-framework-extended',
  'other_gbis-standard_dot-net-framework-sdk',
  'other_gbis-standard_dot-net-memory-profiler',
  'other_gbis-standard_dot-net-reflector',
  'other_gbis-standard_dot-net-runtime',
  'other_gbis-standard_dot-net-sdk',
  'other_gbis-standard_dot-net-sdk-pre-releases',
  'other_gbis-standard_dtrace',
  'other_gbis-standard_eagle',
  'other_gbis-standard_ec',
  'other_gbis-standard_ecl',
  'other_gbis-standard_eiffel',
  'other_gbis-standard_ejs',
  'other_gbis-standard_elixir',
  'other_gbis-standard_elm',
  'other_gbis-standard_emacs-lisp',
  'other_gbis-standard_ems',
  'other_gbis-standard_eq',
  'other_gbis-standard_erlang',
  'other_gbis-standard_euphoria',
  'other_gbis-standard_extrem-application-platform-xap',
  'other_gbis-standard_faust',
  'other_gbis-standard_fluent',
  'other_gbis-standard_flux',
  'other_gbis-standard_forth',
  'other_gbis-standard_fortran',
  'other_gbis-standard_free-basic',
  'other_gbis-standard_free-marker',
  'other_gbis-standard_f-sharp',
  'other_gbis-standard_f-star',
  'other_gbis-standard_futhark',
  'other_gbis-standard_game-maker-language',
  'other_gbis-standard_gap',
  'other_gbis-standard_gcc-machine-description',
  'other_gbis-standard_gdb',
  'other_gbis-standard_gemfire',
  'other_gbis-standard_genero-forms',
  'other_gbis-standard_genshi',
  'other_gbis-standard_gherkin',
  'other_gbis-standard_giga-spaces-xap-dot-net-12-2-1',
  'other_gbis-standard_giga-spaces-xap-dot-net-16-1-0',
  'other_gbis-standard_giga-spaces-xap-dot-net-premium-edition-9-7-2-x86',
  'other_gbis-standard_glsl',
  'other_gbis-standard_gnuplot',
  'other_gbis-standard_go',
  'other_gbis-standard_grammatical-framework',
  'other_gbis-standard_groovy',
  'other_gbis-standard_hack',
  'other_gbis-standard_handlebars',
  'other_gbis-standard_harbour',
  'other_gbis-standard_haskell',
  'other_gbis-standard_hcl',
  'other_gbis-standard_hd-insight-azure',
  'other_gbis-standard_hive-ql',
  'other_gbis-standard_hlsl',
  'other_gbis-standard_html',
  'other_gbis-standard_idl',
  'other_gbis-standard_igor-pro',
  'other_gbis-standard_inno-setup',
  'other_gbis-standard_io',
  'other_gbis-standard_iprocess',
  'other_gbis-standard_iron-python-for-dot-net-4',
  'other_gbis-standard_jasmin',
  'other_gbis-standard_java',
  'other_gbis-standard_java-development-kit',
  'other_gbis-standard_java-platform',
  'other_gbis-standard_java-runtime-environement',
  'other_gbis-standard_javascript',
  'other_gbis-standard_jboss-eap',
  'other_gbis-standard_jboss-wildfly',
  'other_gbis-standard_jinja',
  'other_gbis-standard_jolie',
  'other_gbis-standard_jq',
  'other_gbis-standard_jsonnet',
  'other_gbis-standard_jupyter-notebook',
  'other_gbis-standard_kotlin',
  'other_gbis-standard_kubernetes-cloud-azure',
  'other_gbis-standard_lasso',
  'other_gbis-standard_less',
  'other_gbis-standard_lex',
  'other_gbis-standard_limbo',
  'other_gbis-standard_linux',
  'other_gbis-standard_liquid',
  'other_gbis-standard_llvm',
  'other_gbis-standard_logos',
  'other_gbis-standard_lua',
  'other_gbis-standard_m',
  'other_gbis-standard_m4',
  'other_gbis-standard_makefile',
  'other_gbis-standard_mako',
  'other_gbis-standard_mask',
  'other_gbis-standard_mathematica',
  'other_gbis-standard_matlab',
  'other_gbis-standard_max',
  'other_gbis-standard_mercury',
  'other_gbis-standard_meson',
  'other_gbis-standard_messaging-middleware',
  'other_gbis-standard_mirc-script',
  'other_gbis-standard_modelica',
  'other_gbis-standard_module-management-system',
  'other_gbis-standard_moocode',
  'other_gbis-standard_moon-script',
  'other_gbis-standard_mq',
  'other_gbis-standard_mssql',
  'other_gbis-standard_mustache',
  'other_gbis-standard_myghty',
  'other_gbis-standard_nasl',
  'other_gbis-standard_ncl',
  'other_gbis-standard_new-lisp',
  'other_gbis-standard_nextflow',
  'other_gbis-standard_nginx',
  'other_gbis-standard_nix',
  'other_gbis-standard_nsis',
  'other_gbis-standard_nu',
  'other_gbis-standard_objective-c',
  'other_gbis-standard_objective-cpp',
  'other_gbis-standard_objective-j',
  'other_gbis-standard_ocaml',
  'other_gbis-standard_opa',
  'other_gbis-standard_open-edge-abl',
  'other_gbis-standard_open-java-development-kit',
  'other_gbis-standard_open-jdk-runtime-environement',
  'other_gbis-standard_open-policy-agent',
  'other_gbis-standard_oracle',
  'other_gbis-standard_part-cover-dot-net',
  'other_gbis-standard_pascal',
  'other_gbis-standard_pawn',
  'other_gbis-standard_peoplesoft',
  'other_gbis-standard_perl',
  'other_gbis-standard_perl-6',
  'other_gbis-standard_pgsql-azure',
  'other_gbis-standard_php',
  'other_gbis-standard_physical-server',
  'other_gbis-standard_pig-latin',
  'other_gbis-standard_pl-pgsql',
  'other_gbis-standard_pl-sql',
  'other_gbis-standard_postgresql',
  'other_gbis-standard_post-script',
  'other_gbis-standard_pov-ray-sdl',
  'other_gbis-standard_power-bi-report-server',
  'other_gbis-standard_power-builder',
  'other_gbis-standard_powercenter',
  'other_gbis-standard_power-shell',
  'other_gbis-standard_prolog',
  'other_gbis-standard_protocol-buffer',
  'other_gbis-standard_pug',
  'other_gbis-standard_puppet',
  'other_gbis-standard_pure-basic',
  'other_gbis-standard_pure-data',
  'other_gbis-standard_python',
  'other_gbis-standard_q',
  'other_gbis-standard_qmake',
  'other_gbis-standard_qml',
  'other_gbis-standard_qt-script',
  'other_gbis-standard_r',
  'other_gbis-standard_rabbit-mq',
  'other_gbis-standard_raku',
  'other_gbis-standard_rebol',
  'other_gbis-standard_redcode',
  'other_gbis-standard_rescript',
  'other_gbis-standard_rexx',
  'other_gbis-standard_rich-text-format',
  'other_gbis-standard_robot-framework',
  'other_gbis-standard_roff',
  'other_gbis-standard_rpc',
  'other_gbis-standard_ruby',
  'other_gbis-standard_rust',
  'other_gbis-standard_sas',
  'other_gbis-standard_sass',
  'other_gbis-standard_scala',
  'other_gbis-standard_scheme',
  'other_gbis-standard_scilab',
  'other_gbis-standard_scss',
  'other_gbis-standard_sed',
  'other_gbis-standard_server-gen',
  'other_transformation-path_server-gen-one',
  'other_transformation-path_server-gen-two',
  'other_transformation-path_server-tradi',
  'other_transformation-path_server-azure',
  'other_transformation-path_server-aws',
  'other_gbis-standard_shell',
  'other_gbis-standard_slice',
  'other_gbis-standard_smalltalk',
  'other_gbis-standard_smarty',
  'other_gbis-standard_smpl',
  'other_gbis-standard_source-pawn',
  'other_gbis-standard_sonar',
  'other_gbis-standard_sql',
  'other_gbis-standard_sqlpl',
  'other_gbis-standard_sql-server-analysis-services',
  'other_gbis-standard_sql-server-integration-services',
  'other_gbis-standard_sql-server-reporting-services',
  'other_gbis-standard_srecode-template',
  'other_gbis-standard_standard-ml',
  'other_gbis-standard_starlark',
  'other_gbis-standard_stata',
  'other_gbis-standard_sterling-file-gateway',
  'other_gbis-standard_string-template',
  'other_gbis-standard_stylus',
  'other_gbis-standard_svelte',
  'other_gbis-standard_swift',
  'other_gbis-standard_swig',
  'other_gbis-standard_synchronization-services-for-ado-dot-net',
  'other_gbis-standard_system-verilog',
  'other_gbis-standard_talend-bigdata',
  'other_gbis-standard_tcl',
  'other_gbis-standard_tex',
  'other_gbis-standard_thrift',
  'other_gbis-standard_tibco-rdv-middleware',
  'other_gbis-standard_tla',
  'other_gbis-standard_tomcat',
  'other_gbis-standard_tsa',
  'other_gbis-standard_tsql',
  'other_gbis-standard_tuxedo-dot-net-client',
  'other_gbis-standard_twig',
  'other_gbis-standard_typescript',
  'other_gbis-standard_ui-for-asp-dot-net-mvc',
  'other_gbis-standard_vba',
  'other_gbis-standard_vb-script',
  'other_gbis-standard_vim-script',
  'other_gbis-standard_vim-snippet',
  'other_gbis-standard_vision-for-dot-net',
  'other_gbis-standard_visual-basic',
  'other_gbis-standard_visual-basic-6',
  'other_gbis-standard_visual-basic-dot-net',
  'other_gbis-standard_visual-studio-ado-dot-net-entity-framework-tools',
  'other_gbis-standard_vue',
  'other_gbis-standard_web-assembly',
  'other_gbis-standard_weblogic-server',
  'other_gbis-standard_webmethods',
  'other_gbis-standard_web-middleware',
  'other_gbis-standard_windows',
  'other_gbis-standard_windows-azure-libraries-for-dot-net',
  'other_gbis-standard_xap-dot-net',
  'other_gbis-standard_x-base',
  'other_gbis-standard_x-proc',
  'other_gbis-standard_x-query',
  'other_gbis-standard_xs',
  'other_gbis-standard_xslt',
  'other_gbis-standard_xtend',
  'other_gbis-standard_yacc',
  'other_gbis-standard_zen-script',
  'other_gbis-standard_xonsh',
  'other_gbis-standard_filebench-wml',
  'other_gbis-standard_reason',
  'other_gbis-standard_slash',
  'other_gbis-standard_v',
  'other_gbis-standard_verilog',
  'other_health_changes',
  'other_health_emergency-changes',
  'other_health_incidents',
  'other_health_mim',
  'other_health_sev-0-incidents',
  'other_health_sev-1-incidents',
  'other_health_sev-2-incidents',
  'other_middlewares_autosys-middleware',
  'other_middlewares_messaging-middleware',
  'other_middlewares_tibco-rdv-middleware',
  'other_middlewares_web-middleware',
  'other_obsolescence_abap',
  'other_obsolescence_abcpdf-dot-net',
  'other_obsolescence_action-script',
  'other_obsolescence_ada',
  'other_obsolescence_adaptive-server-entreprise-ado-dot-net-data-provider',
  'other_obsolescence_ado-dot-net',
  'other_obsolescence_ado-dot-net-entity-framework',
  'other_obsolescence_ado-dot-net-entity-framework-tools',
  'other_obsolescence_ags-script',
  'other_obsolescence_al',
  'other_obsolescence_alloy',
  'other_obsolescence_ampl',
  'other_obsolescence_angel-script',
  'other_obsolescence_antlr',
  'other_obsolescence_apache-conf',
  'other_obsolescence_apache-web-server',
  'other_obsolescence_apex',
  'other_obsolescence_api-blueprint',
  'other_obsolescence_apl',
  'other_obsolescence_app-dynamics-dot-net-agent',
  'other_obsolescence_apple-script',
  'other_obsolescence_arc',
  'other_obsolescence_asl',
  'other_obsolescence_asp',
  'other_obsolescence_asp-dot-net',
  'other_obsolescence_asp-dot-net-ajax-extensions',
  'other_obsolescence_asp-dot-net-core-hosting-bundle',
  'other_obsolescence_asp-dot-net-core-runtime',
  'other_obsolescence_asp-dot-net-core-runtime-package-store',
  'other_obsolescence_asp-dot-net-core-runtime-package-store-pre-releases',
  'other_obsolescence_asp-dot-net-core-shared-framework',
  'other_obsolescence_asp-dot-net-core-shared-framework-pre-releases',
  'other_obsolescence_asp-dot-net-frameworkd-and-tools-for-visual-studio-2013',
  'other_obsolescence_asp-dot-net-lang',
  'other_obsolescence_asp-dot-net-mvc',
  'other_obsolescence_asp-dot-net-web-frameworks-and-tools-for-visual-studio-2015',
  'other_obsolescence_aspect-j',
  'other_obsolescence_assembly',
  'other_obsolescence_auto-hoy-key',
  'other_obsolescence_auto-it',
  'other_obsolescence_autosys-middleware',
  'other_obsolescence_awk',
  'other_obsolescence_basic',
  'other_obsolescence_batchfile',
  'other_obsolescence_bicep',
  'other_obsolescence_bikeshed',
  'other_obsolescence_blade',
  'other_obsolescence_blend-for-visual-studio-sdk-for-dot-net',
  'other_obsolescence_boo',
  'other_obsolescence_brighscript',
  'other_obsolescence_brighter-script',
  'other_obsolescence_businessobjects',
  'other_obsolescence_business-works',
  'other_obsolescence_c',
  'other_obsolescence_cap-n-proto',
  'other_obsolescence_carto-css',
  'other_obsolescence_chart-controls-for-microsoft-dot-net-framework',
  'other_obsolescence_clarion',
  'other_obsolescence_classic-asp',
  'other_obsolescence_clean',
  'other_obsolescence_clojure',
  'other_obsolescence_cmake',
  'other_obsolescence_cobol',
  'other_obsolescence_code-contracts-for-dot-net',
  'other_obsolescence_coffee-script',
  'other_obsolescence_cold-fusion',
  'other_obsolescence_common-lisp',
  'other_obsolescence_component-art-ui-framework-for-dot-net',
  'other_obsolescence_connect-express-for-unix',
  'other_obsolescence_cool',
  'other_obsolescence_coq',
  'other_obsolescence_cpp',
  'other_obsolescence_cruise-control-dot-net',
  'other_obsolescence_crystal-reports-for-dot-net-framework',
  'other_obsolescence_crystal-reports-runtime-engine-for-dot-net-framework',
  'other_obsolescence_c-sharp',
  'other_obsolescence_csoud-document',
  'other_obsolescence_csound-score',
  'other_obsolescence_css',
  'other_obsolescence_cuda',
  'other_obsolescence_cython',
  'other_obsolescence_d',
  'other_obsolescence_dart',
  'other_obsolescence_data-provider-for-dot-net',
  'other_obsolescence_developer-express-dot-net',
  'other_obsolescence_digital-command-language',
  'other_obsolescence_dockerfile',
  'other_obsolescence_documentum',
  'other_obsolescence_dot-net-compact-framework',
  'other_obsolescence_dot-net-core-host-pre-releases',
  'other_obsolescence_dot-net-core-runtime',
  'other_obsolescence_dot-net-core-runtime-pre-release',
  'other_obsolescence_dot-net-core-runtime-pre-releases',
  'other_obsolescence_dot-net-core-tools-visual-studio',
  'other_obsolescence_dot-net-core-windows-server-hosting',
  'other_obsolescence_dot-net-framework',
  'other_obsolescence_dot-net-framework-client-profile',
  'other_obsolescence_dot-net-framework-extended',
  'other_obsolescence_dot-net-framework-sdk',
  'other_obsolescence_dot-net-memory-profiler',
  'other_obsolescence_dot-net-reflector',
  'other_obsolescence_dot-net-runtime',
  'other_obsolescence_dot-net-sdk',
  'other_obsolescence_dot-net-sdk-pre-releases',
  'other_obsolescence_dtrace',
  'other_obsolescence_eagle',
  'other_obsolescence_ec',
  'other_obsolescence_ecl',
  'other_obsolescence_eiffel',
  'other_obsolescence_ejs',
  'other_obsolescence_elixir',
  'other_obsolescence_elm',
  'other_obsolescence_emacs-lisp',
  'other_obsolescence_ems',
  'other_obsolescence_eq',
  'other_obsolescence_erlang',
  'other_obsolescence_euphoria',
  'other_obsolescence_extrem-application-platform-xap',
  'other_obsolescence_faust',
  'other_obsolescence_filebench-wml',
  'other_obsolescence_fluent',
  'other_obsolescence_flux',
  'other_obsolescence_forth',
  'other_obsolescence_fortran',
  'other_obsolescence_free-basic',
  'other_obsolescence_free-marker',
  'other_obsolescence_f-sharp',
  'other_obsolescence_f-star',
  'other_obsolescence_futhark',
  'other_obsolescence_game-maker-language',
  'other_obsolescence_gap',
  'other_obsolescence_gcc-machine-description',
  'other_obsolescence_gdb',
  'other_obsolescence_gemfire',
  'other_obsolescence_genero-forms',
  'other_obsolescence_genshi',
  'other_obsolescence_gherkin',
  'other_obsolescence_giga-spaces-xap-dot-net-12-2-1',
  'other_obsolescence_giga-spaces-xap-dot-net-16-1-0',
  'other_obsolescence_giga-spaces-xap-dot-net-premium-edition-9-7-2-x86',
  'other_obsolescence_glsl',
  'other_obsolescence_gnuplot',
  'other_obsolescence_go',
  'other_obsolescence_grammatical-framework',
  'other_obsolescence_groovy',
  'other_obsolescence_hack',
  'other_obsolescence_handlebars',
  'other_obsolescence_harbour',
  'other_obsolescence_haskell',
  'other_obsolescence_hcl',
  'other_obsolescence_hd-insight-azure',
  'other_obsolescence_hive-ql',
  'other_obsolescence_hlsl',
  'other_obsolescence_html',
  'other_obsolescence_idl',
  'other_obsolescence_igor-pro',
  'other_obsolescence_inno-setup',
  'other_obsolescence_io',
  'other_obsolescence_iprocess',
  'other_obsolescence_iron-python-for-dot-net-4',
  'other_obsolescence_jasmin',
  'other_obsolescence_java',
  'other_obsolescence_java-development-kit',
  'other_obsolescence_java-platform',
  'other_obsolescence_java-runtime-environement',
  'other_obsolescence_javascript',
  'other_obsolescence_jboss-eap',
  'other_obsolescence_jboss-wildfly',
  'other_obsolescence_jinja',
  'other_obsolescence_jolie',
  'other_obsolescence_jq',
  'other_obsolescence_jsonnet',
  'other_obsolescence_jupyter-notebook',
  'other_obsolescence_kotlin',
  'other_obsolescence_sonar',
  'other_obsolescence_kubernetes',
  'other_obsolescence_kubernetes-cloud-azure',
  'other_obsolescence_lasso',
  'other_obsolescence_less',
  'other_obsolescence_lex',
  'other_obsolescence_limbo',
  'other_obsolescence_linux',
  'other_obsolescence_liquid',
  'other_obsolescence_llvm',
  'other_obsolescence_logos',
  'other_obsolescence_lua',
  'other_obsolescence_m',
  'other_obsolescence_m4',
  'other_obsolescence_makefile',
  'other_obsolescence_mako',
  'other_obsolescence_mask',
  'other_obsolescence_mathematica',
  'other_obsolescence_matlab',
  'other_obsolescence_max',
  'other_obsolescence_mercury',
  'other_obsolescence_meson',
  'other_obsolescence_messaging-middleware',
  'other_obsolescence_mirc-script',
  'other_obsolescence_modelica',
  'other_obsolescence_module-management-system',
  'other_obsolescence_moocode',
  'other_obsolescence_moon-script',
  'other_obsolescence_mq',
  'other_obsolescence_mssql',
  'other_obsolescence_mustache',
  'other_obsolescence_myghty',
  'other_obsolescence_nasl',
  'other_obsolescence_ncl',
  'other_obsolescence_new-lisp',
  'other_obsolescence_nextflow',
  'other_obsolescence_nginx',
  'other_obsolescence_nix',
  'other_obsolescence_nsis',
  'other_obsolescence_nu',
  'other_obsolescence_objective-c',
  'other_obsolescence_objective-cpp',
  'other_obsolescence_objective-j',
  'other_obsolescence_ocaml',
  'other_obsolescence_opa',
  'other_obsolescence_open-edge-abl',
  'other_obsolescence_open-java-development-kit',
  'other_obsolescence_open-jdk-runtime-environement',
  'other_obsolescence_open-policy-agent',
  'other_obsolescence_oracle',
  'other_obsolescence_part-cover-dot-net',
  'other_obsolescence_pascal',
  'other_obsolescence_pawn',
  'other_obsolescence_peoplesoft',
  'other_obsolescence_perl',
  'other_obsolescence_perl-6',
  'other_obsolescence_pgsql-azure',
  'other_obsolescence_php',
  'other_obsolescence_physical-server',
  'other_obsolescence_pig-latin',
  'other_obsolescence_pl-pgsql',
  'other_obsolescence_pl-sql',
  'other_obsolescence_postgresql',
  'other_obsolescence_post-script',
  'other_obsolescence_pov-ray-sdl',
  'other_obsolescence_power-bi-report-server',
  'other_obsolescence_power-builder',
  'other_obsolescence_powercenter',
  'other_obsolescence_power-shell',
  'other_obsolescence_prolog',
  'other_obsolescence_protocol-buffer',
  'other_obsolescence_pug',
  'other_obsolescence_puppet',
  'other_obsolescence_pure-basic',
  'other_obsolescence_pure-data',
  'other_obsolescence_python',
  'other_obsolescence_q',
  'other_obsolescence_qmake',
  'other_obsolescence_qml',
  'other_obsolescence_qt-script',
  'other_obsolescence_r',
  'other_obsolescence_rabbit-mq',
  'other_obsolescence_raku',
  'other_obsolescence_reason',
  'other_obsolescence_rebol',
  'other_obsolescence_redcode',
  'other_obsolescence_rescript',
  'other_obsolescence_rexx',
  'other_obsolescence_rich-text-format',
  'other_obsolescence_robot-framework',
  'other_obsolescence_roff',
  'other_obsolescence_rpc',
  'other_obsolescence_ruby',
  'other_obsolescence_rust',
  'other_obsolescence_sas',
  'other_obsolescence_sass',
  'other_obsolescence_scala',
  'other_obsolescence_scheme',
  'other_obsolescence_scilab',
  'other_obsolescence_scss',
  'other_obsolescence_sed',
  'other_obsolescence_server-gen',
  'other_obsolescence_shell',
  'other_obsolescence_slash',
  'other_obsolescence_slice',
  'other_obsolescence_smalltalk',
  'other_obsolescence_smarty',
  'other_obsolescence_smpl',
  'other_obsolescence_source-pawn',
  'other_obsolescence_sql',
  'other_obsolescence_sqlpl',
  'other_obsolescence_sql-server-analysis-services',
  'other_obsolescence_sql-server-integration-services',
  'other_obsolescence_sql-server-reporting-services',
  'other_obsolescence_srecode-template',
  'other_obsolescence_standard-ml',
  'other_obsolescence_starlark',
  'other_obsolescence_stata',
  'other_obsolescence_sterling-file-gateway',
  'other_obsolescence_string-template',
  'other_obsolescence_stylus',
  'other_obsolescence_svelte',
  'other_obsolescence_swift',
  'other_obsolescence_swig',
  'other_obsolescence_synchronization-services-for-ado-dot-net',
  'other_obsolescence_system-verilog',
  'other_obsolescence_talend-bigdata',
  'other_obsolescence_tcl',
  'other_obsolescence_tex',
  'other_obsolescence_thrift',
  'other_obsolescence_tibco-rdv-middleware',
  'other_obsolescence_tla',
  'other_obsolescence_tomcat',
  'other_obsolescence_tsa',
  'other_obsolescence_tsql',
  'other_obsolescence_tuxedo-dot-net-client',
  'other_obsolescence_twig',
  'other_obsolescence_typescript',
  'other_obsolescence_ui-for-asp-dot-net-mvc',
  'other_obsolescence_v',
  'other_obsolescence_vba',
  'other_obsolescence_vb-script',
  'other_obsolescence_verilog',
  'other_obsolescence_vim-script',
  'other_obsolescence_vim-snippet',
  'other_obsolescence_vision-for-dot-net',
  'other_obsolescence_visual-basic',
  'other_obsolescence_visual-basic-6',
  'other_obsolescence_visual-basic-dot-net',
  'other_obsolescence_visual-studio-ado-dot-net-entity-framework-tools',
  'other_obsolescence_vue',
  'other_obsolescence_web-assembly',
  'other_obsolescence_weblogic-server',
  'other_obsolescence_webmethods',
  'other_obsolescence_web-middleware',
  'other_obsolescence_windows',
  'other_obsolescence_windows-azure-libraries-for-dot-net',
  'other_obsolescence_xap-dot-net',
  'other_obsolescence_x-base',
  'other_obsolescence_xonsh',
  'other_obsolescence_x-proc',
  'other_obsolescence_x-query',
  'other_obsolescence_xs',
  'other_obsolescence_xslt',
  'other_obsolescence_xtend',
  'other_obsolescence_yacc',
  'other_obsolescence_zen-script',
  'tco_gbsu-cost_non-workforce',
  'tco_gbsu-cost_non-workforce-forecast',
  'tco_gbsu-cost_non-workforce-prev-year',
  'tco_gbsu-cost_workforce',
  'tco_gbsu-cost_workforce-forecast',
  'tco_gbsu-cost_workforce-prev-year',
  'tco_infrastructure-cost_azure',
  'tco_infrastructure-cost_azure-forecast',
  'tco_infrastructure-cost_azure-prev-year',
  'tco_infrastructure-cost_aws',
  'tco_infrastructure-cost_aws-forecast',
  'tco_infrastructure-cost_aws-prev-year',
  'tco_infrastructure-cost_gts',
  'tco_infrastructure-cost_gts-forecast',
  'tco_infrastructure-cost_gts-prev-year',
  'tco_tbs_tbs-enhancement',
  'tco_tbs_tbs-financial',
  'tco_tbs_tbs-infrastructure',
  'tco_tbs_tbs-management',
  'tco_tbs_tbs-project',
  'tco_tbs_tbs-support',
  'tco_tbs_tbs-technical',
  'tco_tbs_tbs-enhancement-duplicate',
  'tco_tbs_tbs-financial-duplicate',
  'tco_tbs_tbs-infrastructure-duplicate',
  'tco_tbs_tbs-management-duplicate',
  'tco_tbs_tbs-project-duplicate',
  'tco_tbs_tbs-support-duplicate',
  'tco_tbs_tbs-technical-duplicate',
  'tco_vendor-cost_software',
  'tco_vendor-cost_software-forecast',
  'tco_vendor-cost_software-prev-year',
  'iAppliName',
  'languages',
  'other_deviance-standard_1c-entreprise',
  'other_deviance-standard_abap',
  'other_deviance-standard_action-script',
  'other_deviance-standard_ada',
  'other_deviance-standard_ags-script',
  'other_deviance-standard_al',
  'other_deviance-standard_alloy',
  'other_deviance-standard_ampl',
  'other_deviance-standard_angel-script',
  'other_deviance-standard_antlr',
  'other_deviance-standard_apache-conf',
  'other_deviance-standard_apex',
  'other_deviance-standard_api-blueprint',
  'other_deviance-standard_apl',
  'other_deviance-standard_apple-script',
  'other_deviance-standard_arc',
  'other_deviance-standard_asl',
  'other_deviance-standard_asp',
  'other_deviance-standard_aspect-j',
  'other_deviance-standard_assembly',
  'other_deviance-standard_auto-hoy-key',
  'other_deviance-standard_auto-it',
  'other_deviance-standard_awk',
  'other_deviance-standard_basic',
  'other_deviance-standard_batchfile',
  'other_deviance-standard_bicep',
  'other_deviance-standard_bikeshed',
  'other_deviance-standard_blade',
  'other_deviance-standard_boo',
  'other_deviance-standard_brainfuck',
  'other_deviance-standard_brighscript',
  'other_deviance-standard_brighter-script',
  'other_deviance-standard_c',
  'other_deviance-standard_c-sharp',
  'other_deviance-standard_cap-n-proto',
  'other_deviance-standard_carto-css',
  'other_deviance-standard_clarion',
  'other_deviance-standard_classic-asp',
  'other_deviance-standard_clean',
  'other_deviance-standard_clojure',
  'other_deviance-standard_cmake',
  'other_deviance-standard_cobol',
  'other_deviance-standard_coffee-script',
  'other_deviance-standard_cold-fusion',
  'other_deviance-standard_common-lisp',
  'other_deviance-standard_cool',
  'other_deviance-standard_coq',
  'other_deviance-standard_corretto',
  'other_deviance-standard_corretto-amazon',
  'other_deviance-standard_cpp',
  'other_deviance-standard_csoud-document',
  'other_deviance-standard_csound-score',
  'other_deviance-standard_css',
  'other_deviance-standard_cuda',
  'other_deviance-standard_cython',
  'other_deviance-standard_d',
  'other_deviance-standard_dart',
  'other_deviance-standard_digital-command-language',
  'other_deviance-standard_dockerfile',
  'other_deviance-standard_dtrace',
  'other_deviance-standard_eagle',
  'other_deviance-standard_ec',
  'other_deviance-standard_ecl',
  'other_deviance-standard_eiffel',
  'other_deviance-standard_ejs',
  'other_deviance-standard_elixir',
  'other_deviance-standard_elm',
  'other_deviance-standard_emacs-lisp',
  'other_deviance-standard_eq',
  'other_deviance-standard_erlang',
  'other_deviance-standard_euphoria',
  'other_deviance-standard_f-sharp',
  'other_deviance-standard_f-star',
  'other_deviance-standard_faust',
  'other_deviance-standard_filebench-wml',
  'other_deviance-standard_fluent',
  'other_deviance-standard_flux',
  'other_deviance-standard_forth',
  'other_deviance-standard_fortran',
  'other_deviance-standard_free-basic',
  'other_deviance-standard_free-marker',
  'other_deviance-standard_frege',
  'other_deviance-standard_futhark',
  'other_deviance-standard_game-maker-language',
  'other_deviance-standard_gap',
  'other_deviance-standard_gcc-machine-description',
  'other_deviance-standard_gdb',
  'other_deviance-standard_gdscript',
  'other_deviance-standard_genero-forms',
  'other_deviance-standard_genshi',
  'other_deviance-standard_gherkin',
  'other_deviance-standard_glsl',
  'other_deviance-standard_gnuplot',
  'other_deviance-standard_go',
  'other_deviance-standard_grammatical-framework',
  'other_deviance-standard_groovy',
  'other_deviance-standard_gsc',
  'other_deviance-standard_hack',
  'other_deviance-standard_handlebars',
  'other_deviance-standard_harbour',
  'other_deviance-standard_haskell',
  'other_deviance-standard_hcl',
  'other_deviance-standard_hive-ql',
  'other_deviance-standard_hlsl',
  'other_deviance-standard_html',
  'other_deviance-standard_idl',
  'other_deviance-standard_igor-pro',
  'other_deviance-standard_inno-setup',
  'other_deviance-standard_io',
  'other_deviance-standard_jasmin',
  'other_deviance-standard_java',
  'other_deviance-standard_javascript',
  'other_deviance-standard_jinja',
  'other_deviance-standard_jolie',
  'other_deviance-standard_jq',
  'other_deviance-standard_jsonnet',
  'other_deviance-standard_jupyter-notebook',
  'other_deviance-standard_kotlin',
  'other_deviance-standard_lasso',
  'other_deviance-standard_less',
  'other_deviance-standard_lex',
  'other_deviance-standard_limbo',
  'other_deviance-standard_liquid',
  'other_deviance-standard_llvm',
  'other_deviance-standard_logos',
  'other_deviance-standard_lua',
  'other_deviance-standard_m',
  'other_deviance-standard_m4',
  'other_deviance-standard_makefile',
  'other_deviance-standard_mako',
  'other_deviance-standard_mask',
  'other_deviance-standard_mathematica',
  'other_deviance-standard_matlab',
  'other_deviance-standard_max',
  'other_deviance-standard_mercury',
  'other_deviance-standard_meson',
  'other_deviance-standard_mirc-script',
  'other_deviance-standard_modelica',
  'other_deviance-standard_module-management-system',
  'other_deviance-standard_monkey-c',
  'other_deviance-standard_moocode',
  'other_deviance-standard_moon-script',
  'other_deviance-standard_mustache',
  'other_deviance-standard_myghty',
  'other_deviance-standard_nasl',
  'other_deviance-standard_ncl',
  'other_deviance-standard_new-lisp',
  'other_deviance-standard_nextflow',
  'other_deviance-standard_nginx',
  'other_deviance-standard_nix',
  'other_deviance-standard_nsis',
  'other_deviance-standard_nu',
  'other_deviance-standard_objective-c',
  'other_deviance-standard_objective-cpp',
  'other_deviance-standard_objective-j',
  'other_deviance-standard_ocaml',
  'other_deviance-standard_opa',
  'other_deviance-standard_open-edge-abl',
  'other_deviance-standard_open-jdk',
  'other_deviance-standard_open-jdk-redhat',
  'other_deviance-standard_open-jdk-runtime-environement',
  'other_deviance-standard_open-jdk-runtime-environment',
  'other_deviance-standard_open-policy-agent',
  'other_deviance-standard_pascal',
  'other_deviance-standard_pawn',
  'other_deviance-standard_perl',
  'other_deviance-standard_perl-6',
  'other_deviance-standard_php',
  'other_deviance-standard_pig-latin',
  'other_deviance-standard_pl-pgsql',
  'other_deviance-standard_pl-sql',
  'other_deviance-standard_post-script',
  'other_deviance-standard_pov-ray-sdl',
  'other_deviance-standard_power-builder',
  'other_deviance-standard_power-shell',
  'other_deviance-standard_prolog',
  'other_deviance-standard_protocol-buffer',
  'other_deviance-standard_pug',
  'other_deviance-standard_puppet',
  'other_deviance-standard_pure-basic',
  'other_deviance-standard_pure-data',
  'other_deviance-standard_python',
  'other_deviance-standard_q',
  'other_deviance-standard_qmake',
  'other_deviance-standard_qml',
  'other_deviance-standard_qt-script',
  'other_deviance-standard_r',
  'other_deviance-standard_raku',
  'other_deviance-standard_rascal',
  'other_deviance-standard_reason',
  'other_deviance-standard_rebol',
  'other_deviance-standard_redcode',
  'other_deviance-standard_render-script',
  'other_deviance-standard_rescript',
  'other_deviance-standard_rexx',
  'other_deviance-standard_rich-text-format',
  'other_deviance-standard_robot-framework',
  'other_deviance-standard_roff',
  'other_deviance-standard_rpc',
  'other_deviance-standard_ruby',
  'other_deviance-standard_rust',
  'other_deviance-standard_sas',
  'other_deviance-standard_sass',
  'other_deviance-standard_scala',
  'other_deviance-standard_scheme',
  'other_deviance-standard_scilab',
  'other_deviance-standard_scss',
  'other_deviance-standard_sed',
  'other_deviance-standard_shell',
  'other_deviance-standard_slash',
  'other_deviance-standard_slice',
  'other_deviance-standard_smalltalk',
  'other_deviance-standard_smarty',
  'other_deviance-standard_smpl',
  'other_deviance-standard_solidity',
  'other_deviance-standard_source-pawn',
  'other_deviance-standard_sqlpl',
  'other_deviance-standard_srecode-template',
  'other_deviance-standard_standard-ml',
  'other_deviance-standard_starlark',
  'other_deviance-standard_stata',
  'other_deviance-standard_string-template',
  'other_deviance-standard_stylus',
  'other_deviance-standard_super-collider',
  'other_deviance-standard_svelte',
  'other_deviance-standard_swift',
  'other_deviance-standard_swig',
  'other_deviance-standard_system-verilog',
  'other_deviance-standard_tcl',
  'other_deviance-standard_temurin-jdk-eclipse-foundation',
  'other_deviance-standard_tex',
  'other_deviance-standard_thrift',
  'other_deviance-standard_tla',
  'other_deviance-standard_tsql',
  'other_deviance-standard_tuxedo-dot-net-client',
  'other_deviance-standard_twig',
  'other_deviance-standard_typescript',
  'other_deviance-standard_v',
  'other_deviance-standard_vb-script',
  'other_deviance-standard_vba',
  'other_deviance-standard_verilog',
  'other_deviance-standard_vim-script',
  'other_deviance-standard_vim-snippet',
  'other_deviance-standard_visual-basic',
  'other_deviance-standard_visual-basic-6',
  'other_deviance-standard_vue',
  'other_deviance-standard_web-assembly',
  'other_deviance-standard_x-base',
  'other_deviance-standard_x-proc',
  'other_deviance-standard_x-query',
  'other_deviance-standard_xonsh',
  'other_deviance-standard_xs',
  'other_deviance-standard_xslt',
  'other_deviance-standard_xtend',
  'other_deviance-standard_yacc',
  'other_deviance-standard_zen-script',
  'other_deviance-standard_zulu-azul-systems',
  'other_deviance-standard_abcpdf-dot-net',
  'other_deviance-standard_acbs-loan-trak',
  'other_deviance-standard_aci-os-network-device',
  'other_deviance-standard_adaptive-server-entreprise-ado-dot-net-data-provider',
  'other_deviance-standard_ado-dot-net',
  'other_deviance-standard_ado-dot-net-entity-framework',
  'other_deviance-standard_ado-dot-net-entity-framework-tools',
  'other_deviance-standard_ag-grid',
  'other_deviance-standard_ag-grid-community-library',
  'other_deviance-standard_ag-grid-enterprise-library',
  'other_deviance-standard_airflow',
  'other_deviance-standard_aix',
  'other_deviance-standard_aix-vm-tradi',
  'other_deviance-standard_alcatel-hardware',
  'other_deviance-standard_algorithmics',
  'other_deviance-standard_angular-library',
  'other_deviance-standard_apache-axis-library',
  'other_deviance-standard_apache-camel-library',
  'other_deviance-standard_apache-commons-library',
  'other_deviance-standard_apache-hbase-middleware',
  'other_deviance-standard_apache-hive-middleware',
  'other_deviance-standard_apache-knox-middleware',
  'other_deviance-standard_apache-livy-middleware',
  'other_deviance-standard_apache-middleware',
  'other_deviance-standard_apache-nifi-middleware',
  'other_deviance-standard_apache-oozie-middleware',
  'other_deviance-standard_apache-ranger-middleware',
  'other_deviance-standard_apache-spark-middleware',
  'other_deviance-standard_apache-web-server',
  'other_deviance-standard_apache-zookeeper-middleware',
  'other_deviance-standard_apic-os-network-device',
  'other_deviance-standard_apim-azure',
  'other_deviance-standard_app-dynamics-dot-net-agent',
  'other_deviance-standard_asp-dot-net',
  'other_deviance-standard_asp-dot-net-ajax-extensions',
  'other_deviance-standard_asp-dot-net-core-hosting-bundle',
  'other_deviance-standard_asp-dot-net-core-runtime',
  'other_deviance-standard_asp-dot-net-core-runtime-package-store',
  'other_deviance-standard_asp-dot-net-core-runtime-package-store-pre-releases',
  'other_deviance-standard_asp-dot-net-core-shared-framework',
  'other_deviance-standard_asp-dot-net-core-shared-framework-pre-releases',
  'other_deviance-standard_asp-dot-net-frameworkd-and-tools-for-visual-studio-2013',
  'other_deviance-standard_asp-dot-net-lang',
  'other_deviance-standard_asp-dot-net-mvc',
  'other_deviance-standard_asp-dot-net-web-frameworks-and-tools-for-visual-studio-2015',
  'other_deviance-standard_asp-versioning-library',
  'other_deviance-standard_aspnetcore-authentication-library',
  'other_deviance-standard_aspnetcore-authorization-library',
  'other_deviance-standard_aspnetcore-blazor-library',
  'other_deviance-standard_aspnetcore-cache-library',
  'other_deviance-standard_aspnetcore-dataprotection-library',
  'other_deviance-standard_aspnetcore-diagnostics-library',
  'other_deviance-standard_aspnetcore-healthchecks-library',
  'other_deviance-standard_aspnetcore-hosting-library',
  'other_deviance-standard_aspnetcore-http-library',
  'other_deviance-standard_aspnetcore-iis-library',
  'other_deviance-standard_aspnetcore-json-library',
  'other_deviance-standard_aspnetcore-kestrel-library',
  'other_deviance-standard_aspnetcore-library',
  'other_deviance-standard_aspnetcore-metadata-library',
  'other_deviance-standard_aspnetcore-mvc-library',
  'other_deviance-standard_aspnetcore-routing-library',
  'other_deviance-standard_aspnetcore-signalr-library',
  'other_deviance-standard_aspnetcore-testing-library',
  'other_deviance-standard_aspnetcore-versioning-library',
  'other_deviance-standard_aspnetcore-webutilities-library',
  'other_deviance-standard_aspose',
  'other_deviance-standard_atlas-middleware',
  'other_deviance-standard_atos-hardware',
  'other_deviance-standard_audiocodes-hardware',
  'other_deviance-standard_autofixture-library',
  'other_deviance-standard_automapper-library',
  'other_deviance-standard_autosys-agent-middleware',
  'other_deviance-standard_autosys-middleware',
  'other_deviance-standard_autosys-server',
  'other_deviance-standard_aws-dotnet-sdk-library',
  'other_deviance-standard_aws-ecs',
  'other_deviance-standard_aws-hardware',
  'other_deviance-standard_aws-java-sdk-v1-library',
  'other_deviance-standard_aws-java-sdk-v2-library',
  'other_deviance-standard_aws-js-sdk-v3-library',
  'other_deviance-standard_aws-vm-linux',
  'other_deviance-standard_aws-vm-windows',
  'other_deviance-standard_axios-library',
  'other_deviance-standard_azure-batch',
  'other_deviance-standard_azure-bot',
  'other_deviance-standard_azure-cdn',
  'other_deviance-standard_azure-cosmos-db',
  'other_deviance-standard_azure-event-hub',
  'other_deviance-standard_azure-front-door',
  'other_deviance-standard_azure-hardware',
  'other_deviance-standard_azure-key-vault',
  'other_deviance-standard_azure-notification-hub',
  'other_deviance-standard_azure-redis-cache',
  'other_deviance-standard_azure-signal-r',
  'other_deviance-standard_azure-storage-accounts',
  'other_deviance-standard_azure-vm-linux',
  'other_deviance-standard_azure-vm-windows',
  'other_deviance-standard_balabit-hardware',
  'other_deviance-standard_blackcore-hardware',
  'other_deviance-standard_blend-for-visual-studio-sdk-for-dot-net',
  'other_deviance-standard_bouncy-castle-library',
  'other_deviance-standard_bull-hardware',
  'other_deviance-standard_business-works',
  'other_deviance-standard_businessobjects',
  'other_deviance-standard_cassandra',
  'other_deviance-standard_cassandra-ring',
  'other_deviance-standard_cestbridge',
  'other_deviance-standard_chart-controls-for-microsoft-dot-net-framework',
  'other_deviance-standard_check-point-hardware',
  'other_deviance-standard_ciara-hardware',
  'other_deviance-standard_circo-ios',
  'other_deviance-standard_circo-ios-vm-tradi',
  'other_deviance-standard_cisco-hardware',
  'other_deviance-standard_citrix-hardware',
  'other_deviance-standard_ckeditor-v5-library',
  'other_deviance-standard_cloudera-data-platform-middleware',
  'other_deviance-standard_cloudera-management-services-middleware',
  'other_deviance-standard_cloudera-manager-middleware',
  'other_deviance-standard_code-contracts-for-dot-net',
  'other_deviance-standard_component-art-ui-framework-for-dot-net',
  'other_deviance-standard_comtech-hardware',
  'other_deviance-standard_connect-express-for-unix',
  'other_deviance-standard_connect-express-for-windows',
  'other_deviance-standard_connecteo',
  'other_deviance-standard_connexion-reasea-a-fix',
  'other_deviance-standard_control-m-agent',
  'other_deviance-standard_controlm-agent',
  'other_deviance-standard_couchbase-cluster',
  'other_deviance-standard_couchbase-server',
  'other_deviance-standard_cruise-control-dot-net',
  'other_deviance-standard_cruise-control-middleware',
  'other_deviance-standard_crystal-reports-for-dot-net-framework',
  'other_deviance-standard_crystal-reports-runtime-engine-for-dot-net-framework',
  'other_deviance-standard_dali-kubernetes',
  'other_deviance-standard_data-provider-for-dot-net',
  'other_deviance-standard_datastage-middleware',
  'other_deviance-standard_db2',
  'other_deviance-standard_dell-emc-hardware',
  'other_deviance-standard_dell-hardware',
  'other_deviance-standard_developer-express-dot-net',
  'other_deviance-standard_didomi',
  'other_deviance-standard_documentum',
  'other_deviance-standard_dot-net-compact-framework',
  'other_deviance-standard_dot-net-core-host-pre-releases',
  'other_deviance-standard_dot-net-core-runtime',
  'other_deviance-standard_dot-net-core-runtime-pre-release',
  'other_deviance-standard_dot-net-core-runtime-pre-releases',
  'other_deviance-standard_dot-net-core-tools-visual-studio',
  'other_deviance-standard_dot-net-core-windows-server-hosting',
  'other_deviance-standard_dot-net-framework',
  'other_deviance-standard_dot-net-framework-client-profile',
  'other_deviance-standard_dot-net-framework-extended',
  'other_deviance-standard_dot-net-framework-sdk',
  'other_deviance-standard_dot-net-memory-profiler',
  'other_deviance-standard_dot-net-reflector',
  'other_deviance-standard_dot-net-runtime',
  'other_deviance-standard_dot-net-sdk',
  'other_deviance-standard_dot-net-sdk-pre-releases',
  'other_deviance-standard_dotnet-runtime-library',
  'other_deviance-standard_dow-jones-new',
  'other_deviance-standard_efficient-ip-hardware',
  'other_deviance-standard_ejs-library',
  'other_deviance-standard_elastic',
  'other_deviance-standard_elastic-apm-library',
  'other_deviance-standard_elastic-nest-library',
  'other_deviance-standard_elasticsearch-dotnet-library',
  'other_deviance-standard_elasticsearch-java-library',
  'other_deviance-standard_elasticsearch-js-library',
  'other_deviance-standard_embedded-esx',
  'other_deviance-standard_embedded-esx-vm-tradi',
  'other_deviance-standard_embedded-os',
  'other_deviance-standard_embedded-os-vm-tradi',
  'other_deviance-standard_ems',
  'other_deviance-standard_entersekt-hardware',
  'other_deviance-standard_entity-framework-core-library',
  'other_deviance-standard_entreprise-architect',
  'other_deviance-standard_eos-network-device',
  'other_deviance-standard_esx',
  'other_deviance-standard_esx-vm-tradi',
  'other_deviance-standard_ethernet-hub-hardware',
  'other_deviance-standard_exalogic-middleware',
  'other_deviance-standard_extjs-library',
  'other_deviance-standard_extrem-application-platform-xap',
  'other_deviance-standard_fat-jar-middleware',
  'other_deviance-standard_fax-software',
  'other_deviance-standard_file-transfer',
  'other_deviance-standard_file-transfer-consumer',
  'other_deviance-standard_file-transfer-producer',
  'other_deviance-standard_fis-saas',
  'other_deviance-standard_fluentassertions-library',
  'other_deviance-standard_fluentvalidation-library',
  'other_deviance-standard_forti-os-network-device',
  'other_deviance-standard_fortinet-hardware',
  'other_deviance-standard_fujitsu-hardware',
  'other_deviance-standard_gaia-network-device',
  'other_deviance-standard_galytix',
  'other_deviance-standard_gemalto-hardware',
  'other_deviance-standard_gemfire',
  'other_deviance-standard_genetec-hardware',
  'other_deviance-standard_giga-spaces-xap-dot-net-12-2-1',
  'other_deviance-standard_giga-spaces-xap-dot-net-16-1-0',
  'other_deviance-standard_giga-spaces-xap-dot-net-premium-edition-9-7-2-x86',
  'other_deviance-standard_gigavue-os-network-device',
  'other_deviance-standard_google-equipment-hardware',
  'other_deviance-standard_grpc-library',
  'other_deviance-standard_gunicorn-middleware',
  'other_deviance-standard_h2-library',
  'other_deviance-standard_hbase-middleware',
  'other_deviance-standard_hd-insight-azure',
  'other_deviance-standard_hdfs-middleware',
  'other_deviance-standard_hp-compaq-digital-equip-hardware',
  'other_deviance-standard_hp-hardware',
  'other_deviance-standard_hpe-hardware',
  'other_deviance-standard_hsqldb-library',
  'other_deviance-standard_hue-middleware',
  'other_deviance-standard_hyperv-hardware',
  'other_deviance-standard_ibm-aix',
  'other_deviance-standard_ibm-hardware',
  'other_deviance-standard_ibmi',
  'other_deviance-standard_ibmi-vm-tradi',
  'other_deviance-standard_icc-hardware',
  'other_deviance-standard_iis-middleware',
  'other_deviance-standard_ilog-cplex',
  'other_deviance-standard_indigovision-hardware',
  'other_deviance-standard_informix',
  'other_deviance-standard_intel-hardware',
  'other_deviance-standard_ios-network-device',
  'other_deviance-standard_ios-xe-network-device',
  'other_deviance-standard_ios-xr-network-device',
  'other_deviance-standard_iprocess',
  'other_deviance-standard_iron-python-for-dot-net-4',
  'other_deviance-standard_jackson-v2-library',
  'other_deviance-standard_jackson-v3-library',
  'other_deviance-standard_jasper',
  'other_deviance-standard_java-development-kit',
  'other_deviance-standard_java-platform',
  'other_deviance-standard_java-platform-for-installer-evidence-oracle',
  'other_deviance-standard_java-platform-public',
  'other_deviance-standard_java-runtime-environement',
  'other_deviance-standard_java-runtime-environment-eclipse-foundation',
  'other_deviance-standard_java-runtime-environment-ibm',
  'other_deviance-standard_java-runtime-environment-oracle',
  'other_deviance-standard_java-runtime-environment-public-oracle',
  'other_deviance-standard_java-runtime-environment-with-hotspot-eclipse-foundation',
  'other_deviance-standard_jboss-eap',
  'other_deviance-standard_jboss-wildfly',
  'other_deviance-standard_jest-library',
  'other_deviance-standard_jetty-library',
  'other_deviance-standard_jide-software',
  'other_deviance-standard_juniper-hardware',
  'other_deviance-standard_kakfa-middleware',
  'other_deviance-standard_komgo',
  'other_deviance-standard_kubernetes-cloud-azure',
  'other_deviance-standard_lambda-aws',
  'other_deviance-standard_lenovo-hardware',
  'other_deviance-standard_lerna-library',
  'other_deviance-standard_lest',
  'other_deviance-standard_library-alglib',
  'other_deviance-standard_light-streamer',
  'other_deviance-standard_linux',
  'other_deviance-standard_linux-network-device',
  'other_deviance-standard_linux-vm-tradi',
  'other_deviance-standard_load-balancer',
  'other_deviance-standard_lodash-library',
  'other_deviance-standard_log4j-library',
  'other_deviance-standard_logrhythm-hardware',
  'other_deviance-standard_mail-jet',
  'other_deviance-standard_mcafee-hardware',
  'other_deviance-standard_messagepack-library',
  'other_deviance-standard_messaging-middleware',
  'other_deviance-standard_micrometer-library',
  'other_deviance-standard_micrometer-tracing-library',
  'other_deviance-standard_microsoft-hardware',
  'other_deviance-standard_mongodb',
  'other_deviance-standard_mongodb-entreprise-server',
  'other_deviance-standard_mq',
  'other_deviance-standard_ms-codeanalysis-library',
  'other_deviance-standard_ms-extensions-caching-library',
  'other_deviance-standard_ms-extensions-configuration-library',
  'other_deviance-standard_ms-extensions-di-library',
  'other_deviance-standard_ms-extensions-diagnostics-library',
  'other_deviance-standard_ms-extensions-healthchecks-library',
  'other_deviance-standard_ms-extensions-hosting-library',
  'other_deviance-standard_ms-extensions-library',
  'other_deviance-standard_ms-extensions-logging-library',
  'other_deviance-standard_ms-extensions-options-library',
  'other_deviance-standard_ms-identity-library',
  'other_deviance-standard_ms-openapi-library',
  'other_deviance-standard_ms-testing-library',
  'other_deviance-standard_ms-testplatform-library',
  'other_deviance-standard_mssql',
  'other_deviance-standard_mysql',
  'other_deviance-standard_na-network-device',
  'other_deviance-standard_ncr-hardware',
  'other_deviance-standard_nestjs-library',
  'other_deviance-standard_netapp-hardware',
  'other_deviance-standard_netty-library',
  'other_deviance-standard_netty-tcnative-library',
  'other_deviance-standard_newtonsoft-json-bson-library',
  'other_deviance-standard_newtonsoft-library',
  'other_deviance-standard_nfluent-analyzer-library',
  'other_deviance-standard_nfluent-library',
  'other_deviance-standard_nginx-middleware',
  'other_deviance-standard_nice-hardware',
  'other_deviance-standard_nifi-middleware',
  'other_deviance-standard_nodejs-middleware',
  'other_deviance-standard_npgsql-library',
  'other_deviance-standard_ns-network-device',
  'other_deviance-standard_nsubstitute-library',
  'other_deviance-standard_numeric',
  'other_deviance-standard_nunit-library',
  'other_deviance-standard_nx-os-network-device',
  'other_deviance-standard_oem-oracle-entreprise-manager-middleware',
  'other_deviance-standard_open-java-development-kit',
  'other_deviance-standard_open-java-development-kit-amazon',
  'other_deviance-standard_open-java-development-kit-eclipse-foundation',
  'other_deviance-standard_open-java-development-kit-oracle',
  'other_deviance-standard_openjdk-runtime-environment-microsoft',
  'other_deviance-standard_openjdk-runtime-environment-oracle',
  'other_deviance-standard_openstack-foundation-hardware',
  'other_deviance-standard_opentelemetry-dotnet-library',
  'other_deviance-standard_opentelemetry-java',
  'other_deviance-standard_opentelemetry-java-library',
  'other_deviance-standard_opentelemetry-js-library',
  'other_deviance-standard_opentext-hardware',
  'other_deviance-standard_oracle',
  'other_deviance-standard_oracle-aws',
  'other_deviance-standard_oracle-dbpaas',
  'other_deviance-standard_oracle-hardware',
  'other_deviance-standard_otd-oracle-traffic-director-middleware',
  'other_deviance-standard_ovh-hardware',
  'other_deviance-standard_part-cover-dot-net',
  'other_deviance-standard_peoplesoft',
  'other_deviance-standard_pgsql-aws',
  'other_deviance-standard_pgsql-azure',
  'other_deviance-standard_pgsql-single-server-azure',
  'other_deviance-standard_phoenix-middleware',
  'other_deviance-standard_planet-hardware',
  'other_deviance-standard_polly-library',
  'other_deviance-standard_postgresql',
  'other_deviance-standard_power-bi-report-server',
  'other_deviance-standard_power-builder-software',
  'other_deviance-standard_powercenter',
  'other_deviance-standard_q-archive',
  'other_deviance-standard_qualys-hardware',
  'other_deviance-standard_quantum-adic-hardware',
  'other_deviance-standard_rabbit-mq',
  'other_deviance-standard_react-dom-library',
  'other_deviance-standard_react-library',
  'other_deviance-standard_redhat-hardware',
  'other_deviance-standard_refit-library',
  'other_deviance-standard_resilience4j-library',
  'other_deviance-standard_rightfax-hardware',
  'other_deviance-standard_riverbed-hardware',
  'other_deviance-standard_rolldown-library',
  'other_deviance-standard_rollup-library',
  'other_deviance-standard_rsa-hardware',
  'other_deviance-standard_s3',
  'other_deviance-standard_safenet-hardware',
  'other_deviance-standard_sagem-hardware',
  'other_deviance-standard_sas-entreprise-miner',
  'other_deviance-standard_script-security-library',
  'other_deviance-standard_serilog-library',
  'other_deviance-standard_server-gen',
  'other_deviance-standard_sft-365',
  'other_deviance-standard_sft-7-zip',
  'other_deviance-standard_sft-a2ps',
  'other_deviance-standard_sft-abrt',
  'other_deviance-standard_sft-access-manager',
  'other_deviance-standard_sft-access-runtime',
  'other_deviance-standard_sft-acl',
  'other_deviance-standard_sft-acrobat-reader',
  'other_deviance-standard_sft-active-data-guard',
  'other_deviance-standard_sft-active-iq-unified-manager',
  'other_deviance-standard_sft-activematrix-adapter-for-database',
  'other_deviance-standard_sft-activematrix-adapter-for-files',
  'other_deviance-standard_sft-activematrix-adapter-for-websphere-mq',
  'other_deviance-standard_sft-activematrix-businessworks',
  'other_deviance-standard_sft-activemq',
  'other_deviance-standard_sft-adapter-sdk',
  'other_deviance-standard_sft-adapter-sdk-56',
  'other_deviance-standard_sft-adaptive-server-anywhere',
  'other_deviance-standard_sft-adaptive-server-enterprise',
  'other_deviance-standard_sft-adaptive-server-enterprise-odbc-driver',
  'other_deviance-standard_sft-adomdnet',
  'other_deviance-standard_sft-advanced-compression',
  'other_deviance-standard_sft-advanced-security',
  'other_deviance-standard_sft-aix',
  'other_deviance-standard_sft-aldon-lifecycle-manager',
  'other_deviance-standard_sft-algo-counterparty-credit-risk',
  'other_deviance-standard_sft-algo-one-commodity-models-add-on',
  'other_deviance-standard_sft-algo-one-core',
  'other_deviance-standard_sft-algo-one-counterparty-credit-risk-base',
  'other_deviance-standard_sft-algo-one-counterparty-credit-risk-cva-add-on',
  'other_deviance-standard_sft-algo-one-credit-derivative-models-add-on',
  'other_deviance-standard_sft-algo-one-developer-add-on',
  'other_deviance-standard_sft-algo-one-foundation',
  'other_deviance-standard_sft-algo-one-market-risk-base',
  'other_deviance-standard_sft-algo-one-risk-application-explorer-add-on',
  'other_deviance-standard_sft-algo-one-stochastic-pricing-models-add-on',
  'other_deviance-standard_sft-alteryx-designer',
  'other_deviance-standard_sft-anaconda',
  'other_deviance-standard_sft-analytic-workspace-manager',
  'other_deviance-standard_sft-ansible',
  'other_deviance-standard_sft-ant',
  'other_deviance-standard_sft-aopalliance',
  'other_deviance-standard_sft-apache-commons-collections',
  'other_deviance-standard_sft-apache-commons-configuration',
  'other_deviance-standard_sft-apache-commons-daemon',
  'other_deviance-standard_sft-apache-commons-dbcp',
  'other_deviance-standard_sft-apache-commons-pool',
  'other_deviance-standard_sft-apache-http-server',
  'other_deviance-standard_sft-apache-web-server-wrapper-component',
  'other_deviance-standard_sft-app-connect-enterprise-for-non-production',
  'other_deviance-standard_sft-application-development-framework',
  'other_deviance-standard_sft-application-express',
  'other_deviance-standard_sft-application-insights-tools-for-visual-studio',
  'other_deviance-standard_sft-application-request-routing-for-iis-7',
  'other_deviance-standard_sft-apr',
  'other_deviance-standard_sft-apr-util',
  'other_deviance-standard_sft-arcgis-runtime-sdk-for-net',
  'other_deviance-standard_sft-array-configuration-utility',
  'other_deviance-standard_sft-array-configuration-utility-cli',
  'other_deviance-standard_sft-asciidoc',
  'other_deviance-standard_sft-atk',
  'other_deviance-standard_sft-atop',
  'other_deviance-standard_sft-autoconf',
  'other_deviance-standard_sft-autoit',
  'other_deviance-standard_sft-automake',
  'other_deviance-standard_sft-autopilot',
  'other_deviance-standard_sft-avahi',
  'other_deviance-standard_sft-aws-command-line-interface',
  'other_deviance-standard_sft-aws-tools-for-windows-powershell',
  'other_deviance-standard_sft-axiom-path-manager',
  'other_deviance-standard_sft-azure-cli',
  'other_deviance-standard_sft-azure-data-studio',
  'other_deviance-standard_sft-azure-information-protection',
  'other_deviance-standard_sft-base-image-script-framework',
  'other_deviance-standard_sft-base-sas',
  'other_deviance-standard_sft-bash',
  'other_deviance-standard_sft-bc',
  'other_deviance-standard_sft-berkeley-db',
  'other_deviance-standard_sft-berkeley-db-linux',
  'other_deviance-standard_sft-beyond-compare',
  'other_deviance-standard_sft-bginfo',
  'other_deviance-standard_sft-binutils',
  'other_deviance-standard_sft-biosdevname',
  'other_deviance-standard_sft-bison',
  'other_deviance-standard_sft-bluez',
  'other_deviance-standard_sft-brasero',
  'other_deviance-standard_sft-bridge-utils',
  'other_deviance-standard_sft-bsf',
  'other_deviance-standard_sft-build-tools',
  'other_deviance-standard_sft-business-intelligence-query',
  'other_deviance-standard_sft-businessobjects-bi-platform',
  'other_deviance-standard_sft-businessobjects-business-intelligence-platform',
  'other_deviance-standard_sft-businessobjects-enterprise',
  'other_deviance-standard_sft-businessobjects-mobile-server',
  'other_deviance-standard_sft-busybox',
  'other_deviance-standard_sft-byacc',
  'other_deviance-standard_sft-bzip2',
  'other_deviance-standard_sft-cairo',
  'other_deviance-standard_sft-calamari',
  'other_deviance-standard_sft-call-level-interface',
  'other_deviance-standard_sft-cassandra',
  'other_deviance-standard_sft-ccrypt',
  'other_deviance-standard_sft-centos',
  'other_deviance-standard_sft-cglib',
  'other_deviance-standard_sft-chrome',
  'other_deviance-standard_sft-cloud-agent',
  'other_deviance-standard_sft-cloud-init',
  'other_deviance-standard_sft-cloud-insights-acquisition-unit',
  'other_deviance-standard_sft-cloud-management-pack-for-oracle-database',
  'other_deviance-standard_sft-clustershell',
  'other_deviance-standard_sft-cntlm',
  'other_deviance-standard_sft-cockpit',
  'other_deviance-standard_sft-cognos-analytics',
  'other_deviance-standard_sft-cognos-analytics-for-non-production-environment',
  'other_deviance-standard_sft-cognos-business-intelligence-software-development-kit',
  'other_deviance-standard_sft-coherence',
  'other_deviance-standard_sft-configuration-management-pack',
  'other_deviance-standard_sft-consul',
  'other_deviance-standard_sft-content-server',
  'other_deviance-standard_sft-control-magent',
  'other_deviance-standard_sft-coreutils',
  'other_deviance-standard_sft-couchbase-server',
  'other_deviance-standard_sft-cpau',
  'other_deviance-standard_sft-cpio',
  'other_deviance-standard_sft-cscope',
  'other_deviance-standard_sft-cups',
  'other_deviance-standard_sft-curl',
  'other_deviance-standard_sft-data-masking-pack',
  'other_deviance-standard_sft-database',
  'other_deviance-standard_sft-database-in-memory',
  'other_deviance-standard_sft-database-lifecycle-management-pack',
  'other_deviance-standard_sft-database-migration-assistant-for-unicode',
  'other_deviance-standard_sft-database-tools',
  'other_deviance-standard_sft-database-vault',
  'other_deviance-standard_sft-db2',
  'other_deviance-standard_sft-db2-advanced-enterprise-server',
  'other_deviance-standard_sft-db2-advanced-workgroup-server',
  'other_deviance-standard_sft-db2-connect',
  'other_deviance-standard_sft-db2-connect-server',
  'other_deviance-standard_sft-db2-workgroup-server',
  'other_deviance-standard_sft-dbus',
  'other_deviance-standard_sft-debug-diagnostics-tool',
  'other_deviance-standard_sft-decision-manager',
  'other_deviance-standard_sft-defender-for-endpoint',
  'other_deviance-standard_sft-deployment-toolkit',
  'other_deviance-standard_sft-designer',
  'other_deviance-standard_sft-developer-tools-for-visual-studio',
  'other_deviance-standard_sft-devextreme',
  'other_deviance-standard_sft-diagnostics-pack',
  'other_deviance-standard_sft-diffutils',
  'other_deviance-standard_sft-directcontrol',
  'other_deviance-standard_sft-distribution-of-apache-cassandra',
  'other_deviance-standard_sft-dmidecode',
  'other_deviance-standard_sft-docker',
  'other_deviance-standard_sft-docker-cli',
  'other_deviance-standard_sft-docker-desktop',
  'other_deviance-standard_sft-documentum',
  'other_deviance-standard_sft-domain-time-ii-audit-server',
  'other_deviance-standard_sft-domain-time-ii-client',
  'other_deviance-standard_sft-domain-time-ii-management-tools',
  'other_deviance-standard_sft-domain-time-ii-server',
  'other_deviance-standard_sft-dos2unix',
  'other_deviance-standard_sft-dotconnect-for-oracle',
  'other_deviance-standard_sft-dotfuscator',
  'other_deviance-standard_sft-doxygen',
  'other_deviance-standard_sft-dsview',
  'other_deviance-standard_sft-dtrace',
  'other_deviance-standard_sft-dvdrw-tools',
  'other_deviance-standard_sft-e2fsprogs',
  'other_deviance-standard_sft-easymock',
  'other_deviance-standard_sft-ed',
  'other_deviance-standard_sft-edge',
  'other_deviance-standard_sft-editran',
  'other_deviance-standard_sft-eikon',
  'other_deviance-standard_sft-elasticsearch',
  'other_deviance-standard_sft-emacs',
  'other_deviance-standard_sft-emacs-nox',
  'other_deviance-standard_sft-emacs-x11',
  'other_deviance-standard_sft-emulex-hba-manager',
  'other_deviance-standard_sft-emulex-hba-manager-enterprise',
  'other_deviance-standard_sft-enchant',
  'other_deviance-standard_sft-endeca-information-discovery-integrator',
  'other_deviance-standard_sft-endpoint-configuration-manager-client',
  'other_deviance-standard_sft-endpoint-protection',
  'other_deviance-standard_sft-enscript',
  'other_deviance-standard_sft-enterprise-agent',
  'other_deviance-standard_sft-enterprise-connect-data-access',
  'other_deviance-standard_sft-enterprise-guide',
  'other_deviance-standard_sft-enterprise-linux',
  'other_deviance-standard_sft-enterprise-message-service',
  'other_deviance-standard_sft-enterprise-miner',
  'other_deviance-standard_sft-environment-manager-agent',
  'other_deviance-standard_sft-erlangotp',
  'other_deviance-standard_sft-escapee',
  'other_deviance-standard_sft-essbase-server',
  'other_deviance-standard_sft-essential-studio',
  'other_deviance-standard_sft-esx-server',
  'other_deviance-standard_sft-ethtool',
  'other_deviance-standard_sft-evince',
  'other_deviance-standard_sft-exadata-storage-server',
  'other_deviance-standard_sft-exadata-system',
  'other_deviance-standard_sft-exchange-server',
  'other_deviance-standard_sft-expat',
  'other_deviance-standard_sft-expect',
  'other_deviance-standard_sft-fail2ban',
  'other_deviance-standard_sft-felix-framework',
  'other_deviance-standard_sft-filebeat',
  'other_deviance-standard_sft-filezilla-client',
  'other_deviance-standard_sft-findutils',
  'other_deviance-standard_sft-firefox',
  'other_deviance-standard_sft-firewalld',
  'other_deviance-standard_sft-fireworks',
  'other_deviance-standard_sft-flexera-oracle-fmw-recognition-application',
  'other_deviance-standard_sft-fontconfig',
  'other_deviance-standard_sft-foomatic',
  'other_deviance-standard_sft-forms-and-reports',
  'other_deviance-standard_sft-freetds',
  'other_deviance-standard_sft-freetype',
  'other_deviance-standard_sft-fslogix-apps',
  'other_deviance-standard_sft-fuse',
  'other_deviance-standard_sft-gawk',
  'other_deviance-standard_sft-gcc',
  'other_deviance-standard_sft-gcc-x8664-linux-gnu',
  'other_deviance-standard_sft-gconf2',
  'other_deviance-standard_sft-gcs-windows-client',
  'other_deviance-standard_sft-gdb',
  'other_deviance-standard_sft-gdm',
  'other_deviance-standard_sft-geekbench',
  'other_deviance-standard_sft-geforce-experience',
  'other_deviance-standard_sft-geoip',
  'other_deviance-standard_sft-gettext',
  'other_deviance-standard_sft-ghostscript',
  'other_deviance-standard_sft-giflib',
  'other_deviance-standard_sft-git',
  'other_deviance-standard_sft-git-core',
  'other_deviance-standard_sft-git-extensions',
  'other_deviance-standard_sft-github-desktop',
  'other_deviance-standard_sft-glib',
  'other_deviance-standard_sft-glibc',
  'other_deviance-standard_sft-glogg',
  'other_deviance-standard_sft-gmp',
  'other_deviance-standard_sft-gnome-power-manager',
  'other_deviance-standard_sft-gnome-terminal',
  'other_deviance-standard_sft-gnu-gettext',
  'other_deviance-standard_sft-gnu-nano',
  'other_deviance-standard_sft-gnupg',
  'other_deviance-standard_sft-gnutls',
  'other_deviance-standard_sft-gobject-introspection',
  'other_deviance-standard_sft-goldengate',
  'other_deviance-standard_sft-gpgme',
  'other_deviance-standard_sft-gpoadmin',
  'other_deviance-standard_sft-grafana',
  'other_deviance-standard_sft-graphviz',
  'other_deviance-standard_sft-grep',
  'other_deviance-standard_sft-groff',
  'other_deviance-standard_sft-groff-base',
  'other_deviance-standard_sft-gstreamer',
  'other_deviance-standard_sft-gtk',
  'other_deviance-standard_sft-gzip',
  'other_deviance-standard_sft-hawk',
  'other_deviance-standard_sft-hawk-snmp-adapter',
  'other_deviance-standard_sft-hdparm',
  'other_deviance-standard_sft-hive-odbc-driver',
  'other_deviance-standard_sft-hpc-pack-client',
  'other_deviance-standard_sft-html-help-workshop',
  'other_deviance-standard_sft-htop',
  'other_deviance-standard_sft-http-server',
  'other_deviance-standard_sft-httpcomponents-client',
  'other_deviance-standard_sft-httpcomponents-core',
  'other_deviance-standard_sft-hunspell',
  'other_deviance-standard_sft-i-access-family',
  'other_deviance-standard_sft-ibus',
  'other_deviance-standard_sft-ice',
  'other_deviance-standard_sft-identity-management-for-unix',
  'other_deviance-standard_sft-idoctor-for-ibm-i',
  'other_deviance-standard_sft-iis-crypto',
  'other_deviance-standard_sft-iis-url-rewrite-module',
  'other_deviance-standard_sft-imagemagick',
  'other_deviance-standard_sft-imlib',
  'other_deviance-standard_sft-informatica',
  'other_deviance-standard_sft-infoscale',
  'other_deviance-standard_sft-infrastruxure-manager-client',
  'other_deviance-standard_sft-instant-client',
  'other_deviance-standard_sft-instant-client-basic',
  'other_deviance-standard_sft-internet-explorer',
  'other_deviance-standard_sft-internet-information-services',
  'other_deviance-standard_sft-investgo',
  'other_deviance-standard_sft-iptables',
  'other_deviance-standard_sft-iq',
  'other_deviance-standard_sft-iq-server-suite',
  'other_deviance-standard_sft-jakarta-commons-beanutils',
  'other_deviance-standard_sft-jakarta-commons-el',
  'other_deviance-standard_sft-jakarta-commons-fileupload',
  'other_deviance-standard_sft-jansson',
  'other_deviance-standard_sft-jasper',
  'other_deviance-standard_sft-java-development-kit-public',
  'other_deviance-standard_sft-java-media-framework',
  'other_deviance-standard_sft-java-plug-in',
  'other_deviance-standard_sft-java-se-development-kit',
  'other_deviance-standard_sft-java-service-wrapper',
  'other_deviance-standard_sft-java-virtual-machine',
  'other_deviance-standard_sft-javamail',
  'other_deviance-standard_sft-jazz-team-server',
  'other_deviance-standard_sft-jboss-enterprise-application-platform',
  'other_deviance-standard_sft-jboss-enterprise-application-server',
  'other_deviance-standard_sft-jboss-jaxrpc-api',
  'other_deviance-standard_sft-jdom',
  'other_deviance-standard_sft-jpeg',
  'other_deviance-standard_sft-jrockit-jdk',
  'other_deviance-standard_sft-jrockit-mission-control-for-java-se',
  'other_deviance-standard_sft-jsch',
  'other_deviance-standard_sft-json-glib',
  'other_deviance-standard_sft-jwhois',
  'other_deviance-standard_sft-jzlib',
  'other_deviance-standard_sft-kibana',
  'other_deviance-standard_sft-krb5-workstation',
  'other_deviance-standard_sft-kubernetes-engine',
  'other_deviance-standard_sft-label-security',
  'other_deviance-standard_sft-lansa',
  'other_deviance-standard_sft-less',
  'other_deviance-standard_sft-libarchive',
  'other_deviance-standard_sft-libasyncns',
  'other_deviance-standard_sft-libattr',
  'other_deviance-standard_sft-libcroco',
  'other_deviance-standard_sft-libcurl',
  'other_deviance-standard_sft-libedit',
  'other_deviance-standard_sft-libexif',
  'other_deviance-standard_sft-libgcc',
  'other_deviance-standard_sft-libgcrypt',
  'other_deviance-standard_sft-libjpeg',
  'other_deviance-standard_sft-libjpeg-turbo',
  'other_deviance-standard_sft-libnetfilterconntrack',
  'other_deviance-standard_sft-libpcap',
  'other_deviance-standard_sft-libpng',
  'other_deviance-standard_sft-libreoffice',
  'other_deviance-standard_sft-libreport',
  'other_deviance-standard_sft-libssh',
  'other_deviance-standard_sft-libtiff',
  'other_deviance-standard_sft-libtool',
  'other_deviance-standard_sft-libvpx',
  'other_deviance-standard_sft-libxcb',
  'other_deviance-standard_sft-libxcrypt',
  'other_deviance-standard_sft-libxfixes',
  'other_deviance-standard_sft-libxml',
  'other_deviance-standard_sft-libxslt',
  'other_deviance-standard_sft-libzmq',
  'other_deviance-standard_sft-license-metric-tool',
  'other_deviance-standard_sft-license-metric-tool---server',
  'other_deviance-standard_sft-lights-out-online-configuration-utility',
  'other_deviance-standard_sft-linux',
  'other_deviance-standard_sft-linux-release',
  'other_deviance-standard_sft-log-parser',
  'other_deviance-standard_sft-log4cxx',
  'other_deviance-standard_sft-log4j',
  'other_deviance-standard_sft-logstash',
  'other_deviance-standard_sft-lpar2rrd-agent',
  'other_deviance-standard_sft-lsof',
  'other_deviance-standard_sft-lua',
  'other_deviance-standard_sft-lzo',
  'other_deviance-standard_sft-m4',
  'other_deviance-standard_sft-mailx',
  'other_deviance-standard_sft-make',
  'other_deviance-standard_sft-mariadb',
  'other_deviance-standard_sft-maven',
  'other_deviance-standard_sft-maven-javadoc',
  'other_deviance-standard_sft-mdadm',
  'other_deviance-standard_sft-merge',
  'other_deviance-standard_sft-mesa-libgl',
  'other_deviance-standard_sft-mesa-libglu',
  'other_deviance-standard_sft-message-analyzer',
  'other_deviance-standard_sft-message-passing-interface',
  'other_deviance-standard_sft-metaframe',
  'other_deviance-standard_sft-metricbeat',
  'other_deviance-standard_sft-microstrategy',
  'other_deviance-standard_sft-miniconda3',
  'other_deviance-standard_sft-mobaxterm',
  'other_deviance-standard_sft-mongodb',
  'other_deviance-standard_sft-moreutils',
  'other_deviance-standard_sft-mozilla-firefox',
  'other_deviance-standard_sft-mpfr',
  'other_deviance-standard_sft-mq',
  'other_deviance-standard_sft-mq-advanced-for-non-production-environment',
  'other_deviance-standard_sft-mremoteng',
  'other_deviance-standard_sft-msdn-library-for-visual-studio',
  'other_deviance-standard_sft-multitenant',
  'other_deviance-standard_sft-mutt',
  'other_deviance-standard_sft-mysql',
  'other_deviance-standard_sft-mysql-connectorodbc',
  'other_deviance-standard_sft-nagios',
  'other_deviance-standard_sft-nano',
  'other_deviance-standard_sft-ncurses',
  'other_deviance-standard_sft-ncurses-base',
  'other_deviance-standard_sft-nekohtml',
  'other_deviance-standard_sft-net-framework-developer-pack',
  'other_deviance-standard_sft-net-snmp',
  'other_deviance-standard_sft-netbackup',
  'other_deviance-standard_sft-netbackup-client',
  'other_deviance-standard_sft-netbackup-server',
  'other_deviance-standard_sft-netprobe',
  'other_deviance-standard_sft-network-connections',
  'other_deviance-standard_sft-network-monitor',
  'other_deviance-standard_sft-network-monitor-parsers',
  'other_deviance-standard_sft-networker',
  'other_deviance-standard_sft-networker-module-for-microsoft-applications',
  'other_deviance-standard_sft-networker-server',
  'other_deviance-standard_sft-networker-storage-node',
  'other_deviance-standard_sft-networkmanager',
  'other_deviance-standard_sft-neuralstar',
  'other_deviance-standard_sft-newt',
  'other_deviance-standard_sft-nginx',
  'other_deviance-standard_sft-nice-engage',
  'other_deviance-standard_sft-nmap-network-mapper',
  'other_deviance-standard_sft-nmon',
  'other_deviance-standard_sft-notepad',
  'other_deviance-standard_sft-notepad-portable',
  'other_deviance-standard_sft-npcap',
  'other_deviance-standard_sft-npgsql',
  'other_deviance-standard_sft-npm',
  'other_deviance-standard_sft-nsclient',
  'other_deviance-standard_sft-nss',
  'other_deviance-standard_sft-ntp',
  'other_deviance-standard_sft-nuget',
  'other_deviance-standard_sft-objects-for-ole',
  'other_deviance-standard_sft-octopus-deploy',
  'other_deviance-standard_sft-octopus-tentacle',
  'other_deviance-standard_sft-odata-source-for-sql-server',
  'other_deviance-standard_sft-odbc-driver-for-sql-server',
  'other_deviance-standard_sft-office',
  'other_deviance-standard_sft-office-access-database-engine',
  'other_deviance-standard_sft-office-ltsc',
  'other_deviance-standard_sft-office-outlook',
  'other_deviance-standard_sft-office-proofing-tools',
  'other_deviance-standard_sft-olap',
  'other_deviance-standard_sft-on-premises-data-gateway',
  'other_deviance-standard_sft-onedrive',
  'other_deviance-standard_sft-open-object-rexx',
  'other_deviance-standard_sft-open-server',
  'other_deviance-standard_sft-open-xml-sdk',
  'other_deviance-standard_sft-openipmi',
  'other_deviance-standard_sft-openj9',
  'other_deviance-standard_sft-openjdk-jre',
  'other_deviance-standard_sft-openjpeg',
  'other_deviance-standard_sft-openldap',
  'other_deviance-standard_sft-openmotif',
  'other_deviance-standard_sft-openonload',
  'other_deviance-standard_sft-openpages-grc-platform',
  'other_deviance-standard_sft-openssh',
  'other_deviance-standard_sft-openssh-clients',
  'other_deviance-standard_sft-openssh-server',
  'other_deviance-standard_sft-openssl',
  'other_deviance-standard_sft-openwebstart',
  'other_deviance-standard_sft-opus',
  'other_deviance-standard_sft-oracle-database',
  'other_deviance-standard_sft-oracle-services-for-microsoft-transaction-server',
  'other_deviance-standard_sft-p7zip',
  'other_deviance-standard_sft-pamkrb5',
  'other_deviance-standard_sft-pango',
  'other_deviance-standard_sft-parted',
  'other_deviance-standard_sft-partitioning',
  'other_deviance-standard_sft-pcre',
  'other_deviance-standard_sft-pdfcreator',
  'other_deviance-standard_sft-perl',
  'other_deviance-standard_sft-perl-archive-zip',
  'other_deviance-standard_sft-perl-constant',
  'other_deviance-standard_sft-perl-http-tiny',
  'other_deviance-standard_sft-perl-xml-simple',
  'other_deviance-standard_sft-pgadmin-4',
  'other_deviance-standard_sft-phpstorm',
  'other_deviance-standard_sft-pigz',
  'other_deviance-standard_sft-pilot-link',
  'other_deviance-standard_sft-pinfo',
  'other_deviance-standard_sft-pipe-viewer',
  'other_deviance-standard_sft-pixman',
  'other_deviance-standard_sft-pkgconfig',
  'other_deviance-standard_sft-pkzip-for-windows',
  'other_deviance-standard_sft-plan-explorer',
  'other_deviance-standard_sft-plexus-compiler',
  'other_deviance-standard_sft-plexus-resources',
  'other_deviance-standard_sft-plsql-developer',
  'other_deviance-standard_sft-policycoreutils',
  'other_deviance-standard_sft-postfix',
  'other_deviance-standard_sft-postgresql',
  'other_deviance-standard_sft-postgresql-jdbc',
  'other_deviance-standard_sft-postgresql-server',
  'other_deviance-standard_sft-postman',
  'other_deviance-standard_sft-power-bi',
  'other_deviance-standard_sft-powerexchange',
  'other_deviance-standard_sft-powerha-systemmirror',
  'other_deviance-standard_sft-powerpath',
  'other_deviance-standard_sft-powerpath-migration-enabler',
  'other_deviance-standard_sft-powertop',
  'other_deviance-standard_sft-privileged-session-manager-ssh-proxy',
  'other_deviance-standard_sft-procmail',
  'other_deviance-standard_sft-professional-service',
  'other_deviance-standard_sft-proftpd',
  'other_deviance-standard_sft-programmer',
  'other_deviance-standard_sft-proliant-integrated-management-log-viewer',
  'other_deviance-standard_sft-protect',
  'other_deviance-standard_sft-protection-engine',
  'other_deviance-standard_sft-provisioning-services-manager-for-linux',
  'other_deviance-standard_sft-proxifier',
  'other_deviance-standard_sft-psqlodbc',
  'other_deviance-standard_sft-psutils',
  'other_deviance-standard_sft-puppet-development-kit',
  'other_deviance-standard_sft-puppetdb',
  'other_deviance-standard_sft-pycairo',
  'other_deviance-standard_sft-pycharm',
  'other_deviance-standard_sft-pygtk',
  'other_deviance-standard_sft-pyopenssl',
  'other_deviance-standard_sft-python',
  'other_deviance-standard_sft-python-pyasn1',
  'other_deviance-standard_sft-python-simplejson',
  'other_deviance-standard_sft-python-six',
  'other_deviance-standard_sft-python-virtualenv',
  'other_deviance-standard_sft-qconvergeconsole-cli',
  'other_deviance-standard_sft-qt',
  'other_deviance-standard_sft-quagga',
  'other_deviance-standard_sft-r',
  'other_deviance-standard_sft-rabbitmq',
  'other_deviance-standard_sft-rational-clearcase',
  'other_deviance-standard_sft-rational-clearcase-on-aix-for-power',
  'other_deviance-standard_sft-rational-jazz-team-server',
  'other_deviance-standard_sft-rational-programming-patterns',
  'other_deviance-standard_sft-rational-purifyplus-for-unix',
  'other_deviance-standard_sft-rcs',
  'other_deviance-standard_sft-real-application-clusters',
  'other_deviance-standard_sft-real-application-testing',
  'other_deviance-standard_sft-redis-on-windows',
  'other_deviance-standard_sft-reflection-for-secure-it-client',
  'other_deviance-standard_sft-reflection-for-secure-it-server',
  'other_deviance-standard_sft-reflection-secure-ftp-client',
  'other_deviance-standard_sft-relativity-data-client',
  'other_deviance-standard_sft-remote-tools-for-visual-studio',
  'other_deviance-standard_sft-rendezvous',
  'other_deviance-standard_sft-replication-server',
  'other_deviance-standard_sft-replication-server-data-assurance-option',
  'other_deviance-standard_sft-report-viewer-redistributable',
  'other_deviance-standard_sft-report-viewer-runtime',
  'other_deviance-standard_sft-rh-postgresql-server',
  'other_deviance-standard_sft-rhino',
  'other_deviance-standard_sft-rkhunter',
  'other_deviance-standard_sft-robo-3t',
  'other_deviance-standard_sft-rsa-archer-grc-platform',
  'other_deviance-standard_sft-rstudio',
  'other_deviance-standard_sft-rsync',
  'other_deviance-standard_sft-rsyslog',
  'other_deviance-standard_sft-rtools',
  'other_deviance-standard_sft-ruby',
  'other_deviance-standard_sft-rubygem-json',
  'other_deviance-standard_sft-rvtools',
  'other_deviance-standard_sft-safenet-hsm-client',
  'other_deviance-standard_sft-safesign',
  'other_deviance-standard_sft-samba',
  'other_deviance-standard_sft-samba-client',
  'other_deviance-standard_sft-sas-studio',
  'other_deviance-standard_sft-sas-system',
  'other_deviance-standard_sft-sbcl',
  'other_deviance-standard_sft-scanmail-for-microsoft-exchange',
  'other_deviance-standard_sft-screenpressor',
  'other_deviance-standard_sft-secrets-manager-credential-providers',
  'other_deviance-standard_sft-security-center',
  'other_deviance-standard_sft-security-key-lifecycle-manager',
  'other_deviance-standard_sft-sed',
  'other_deviance-standard_sft-sendmail',
  'other_deviance-standard_sft-sentinel',
  'other_deviance-standard_sft-service-architecture-leveraging-tuxedo',
  'other_deviance-standard_sft-service-provider',
  'other_deviance-standard_sft-shared-mime-info',
  'other_deviance-standard_sft-sharepoint-online-management-shell',
  'other_deviance-standard_sft-shockwave-player',
  'other_deviance-standard_sft-silverlight',
  'other_deviance-standard_sft-silverlight-sdk',
  'other_deviance-standard_sft-silverlight-toolkit',
  'other_deviance-standard_sft-slirp4netns',
  'other_deviance-standard_sft-smart-array-sassata-event-notification-service',
  'other_deviance-standard_sft-smart-storage-administrator-cli',
  'other_deviance-standard_sft-smart-update-manager',
  'other_deviance-standard_sft-smartmontools',
  'other_deviance-standard_sft-snakeyaml',
  'other_deviance-standard_sft-snapdrive',
  'other_deviance-standard_sft-snapmanager-for-microsoft-exchange',
  'other_deviance-standard_sft-snare',
  'other_deviance-standard_sft-snmp-agent',
  'other_deviance-standard_sft-solaris',
  'other_deviance-standard_sft-sox',
  'other_deviance-standard_sft-spectrum-protect',
  'other_deviance-standard_sft-spectrum-protect-client',
  'other_deviance-standard_sft-spectrum-protect-data-protection-for-oracle',
  'other_deviance-standard_sft-spectrum-protect-for-databases---data-protection-for-microsoft-sql-server',
  'other_deviance-standard_sft-spectrum-protect-for-databases---sql-server',
  'other_deviance-standard_sft-spectrum-protect-for-storage-area-networks',
  'other_deviance-standard_sft-spectrum-scale-client',
  'other_deviance-standard_sft-spectrum-scale-file-placement-optimizer',
  'other_deviance-standard_sft-spectrum-scale-server',
  'other_deviance-standard_sft-spectrum-symphony',
  'other_deviance-standard_sft-sql-anywhere',
  'other_deviance-standard_sft-sql-developer',
  'other_deviance-standard_sft-sql-server-analysis-services',
  'other_deviance-standard_sft-sql-server-command-line-utilities',
  'other_deviance-standard_sft-sql-server-compact',
  'other_deviance-standard_sft-sql-server-data-quality-service',
  'other_deviance-standard_sft-sql-server-data-tier-application-framework',
  'other_deviance-standard_sft-sql-server-database-engine-services',
  'other_deviance-standard_sft-sql-server-integration-services',
  'other_deviance-standard_sft-sql-server-integration-services-projects',
  'other_deviance-standard_sft-sql-server-localdb',
  'other_deviance-standard_sft-sql-server-reporting-services',
  'other_deviance-standard_sft-sqlite',
  'other_deviance-standard_sft-sqlyog',
  'other_deviance-standard_sft-squashfs-tools',
  'other_deviance-standard_sft-ssh-server',
  'other_deviance-standard_sft-ssmtp',
  'other_deviance-standard_sft-sssd',
  'other_deviance-standard_sft-sterling-b2b-integrator',
  'other_deviance-standard_sft-sterling-b2b-integrator-for-non-production-environment',
  'other_deviance-standard_sft-sterling-connect',
  'other_deviance-standard_sft-sterling-connectdirect',
  'other_deviance-standard_sft-sterling-connectexpress',
  'other_deviance-standard_sft-sterling-connectexpress-for-non-production-environment',
  'other_deviance-standard_sft-sterling-connectexpress-non-production-environment',
  'other_deviance-standard_sft-sterling-file-gateway',
  'other_deviance-standard_sft-sterling-secure-proxy-engine',
  'other_deviance-standard_sft-sterling-secure-proxy-for-non-production-environment',
  'other_deviance-standard_sft-sterling-transformation-extender-design-studio',
  'other_deviance-standard_sft-storage-command-line-interface',
  'other_deviance-standard_sft-storage-foundation',
  'other_deviance-standard_sft-storage-foundation-for-oracle',
  'other_deviance-standard_sft-storage-foundation-ha',
  'other_deviance-standard_sft-strace',
  'other_deviance-standard_sft-strawberry-perl',
  'other_deviance-standard_sft-stream',
  'other_deviance-standard_sft-subversion',
  'other_deviance-standard_sft-sudo',
  'other_deviance-standard_sft-sumatrapdf',
  'other_deviance-standard_sft-svg-viewer',
  'other_deviance-standard_sft-swig',
  'other_deviance-standard_sft-sybase',
  'other_deviance-standard_sft-sybase-enterprise-connect-data-access-option-for-oracle',
  'other_deviance-standard_sft-sybase-replication-server-data-assurance-server',
  'other_deviance-standard_sft-sync-framework',
  'other_deviance-standard_sft-syslog-ng',
  'other_deviance-standard_sft-sysstat',
  'other_deviance-standard_sft-system-management-homepage',
  'other_deviance-standard_sft-system-update',
  'other_deviance-standard_sft-systems-director-agent',
  'other_deviance-standard_sft-systemtap',
  'other_deviance-standard_sft-systemtap-client',
  'other_deviance-standard_sft-t1lib',
  'other_deviance-standard_sft-tar',
  'other_deviance-standard_sft-tcpdump',
  'other_deviance-standard_sft-teamcity',
  'other_deviance-standard_sft-teamcity-build-agent',
  'other_deviance-standard_sft-teams-classic',
  'other_deviance-standard_sft-teamviewer',
  'other_deviance-standard_sft-telegraf',
  'other_deviance-standard_sft-temurin-jre',
  'other_deviance-standard_sft-terraform',
  'other_deviance-standard_sft-testcomplete',
  'other_deviance-standard_sft-texinfo',
  'other_deviance-standard_sft-textpad',
  'other_deviance-standard_sft-thunderbird',
  'other_deviance-standard_sft-tomcat',
  'other_deviance-standard_sft-tortoisegit',
  'other_deviance-standard_sft-tortoisesvn',
  'other_deviance-standard_sft-totem',
  'other_deviance-standard_sft-touchpad',
  'other_deviance-standard_sft-transformation-extender-design-studio',
  'other_deviance-standard_sft-transformation-extender-for-integration-servers',
  'other_deviance-standard_sft-transformation-extender-runtime-and-monitoring',
  'other_deviance-standard_sft-transformation-extender-with-launcher',
  'other_deviance-standard_sft-treasure-agent-td-agent',
  'other_deviance-standard_sft-tree',
  'other_deviance-standard_sft-treesize',
  'other_deviance-standard_sft-trousers',
  'other_deviance-standard_sft-truesight-capacity-optimization',
  'other_deviance-standard_sft-tuned',
  'other_deviance-standard_sft-tuning-pack',
  'other_deviance-standard_sft-typescript',
  'other_deviance-standard_sft-unicenter-nsm',
  'other_deviance-standard_sft-unixodbc',
  'other_deviance-standard_sft-unzip',
  'other_deviance-standard_sft-user-state-migration-tool',
  'other_deviance-standard_sft-vcenter-converter',
  'other_deviance-standard_sft-vcenter-server',
  'other_deviance-standard_sft-vcxsrv',
  'other_deviance-standard_sft-veritas-cluster-server',
  'other_deviance-standard_sft-veritas-dynamic-multipathing',
  'other_deviance-standard_sft-veritas-enterprise-administrator',
  'other_deviance-standard_sft-veritas-volume-manager',
  'other_deviance-standard_sft-vim',
  'other_deviance-standard_sft-vim-enhanced',
  'other_deviance-standard_sft-vim-minimal',
  'other_deviance-standard_sft-vim-x11',
  'other_deviance-standard_sft-vinagre',
  'other_deviance-standard_sft-virtual-apps',
  'other_deviance-standard_sft-virtual-apps-and-desktops',
  'other_deviance-standard_sft-virtual-enforcement-node',
  'other_deviance-standard_sft-visibroker',
  'other_deviance-standard_sft-visual-c',
  'other_deviance-standard_sft-visual-c-redistributable',
  'other_deviance-standard_sft-visual-studio',
  'other_deviance-standard_sft-visual-studio-code',
  'other_deviance-standard_sft-visual-studio-sdk',
  'other_deviance-standard_sft-visual-studio-shell',
  'other_deviance-standard_sft-visual-studio-team-explorer',
  'other_deviance-standard_sft-visual-studio-tools-for-applications',
  'other_deviance-standard_sft-visual-studio-tools-for-office-runtime',
  'other_deviance-standard_sft-visualcron',
  'other_deviance-standard_sft-visualsvn-server',
  'other_deviance-standard_sft-vm-for-java',
  'other_deviance-standard_sft-vmware-vix',
  'other_deviance-standard_sft-volume-activation-management-tool',
  'other_deviance-standard_sft-vsphere',
  'other_deviance-standard_sft-vsphere-cli',
  'other_deviance-standard_sft-web-deploy',
  'other_deviance-standard_sft-web-deployment-tool',
  'other_deviance-standard_sft-web-platform-installer',
  'other_deviance-standard_sft-web-publishing-wizard',
  'other_deviance-standard_sft-web-services-enhancements',
  'other_deviance-standard_sft-weblogic-platform',
  'other_deviance-standard_sft-weblogic-server',
  'other_deviance-standard_sft-websphere-adapter-for-email',
  'other_deviance-standard_sft-websphere-adapter-for-ftp',
  'other_deviance-standard_sft-websphere-adapter-for-siebel-business-applications',
  'other_deviance-standard_sft-websphere-adapters-on-websphere-application-server',
  'other_deviance-standard_sft-websphere-application-server',
  'other_deviance-standard_sft-websphere-application-server-liberty',
  'other_deviance-standard_sft-websphere-application-server-liberty-core',
  'other_deviance-standard_sft-websphere-application-server-liberty-ilan',
  'other_deviance-standard_sft-websphere-application-server-network-deployment',
  'other_deviance-standard_sft-websphere-datastage',
  'other_deviance-standard_sft-websphere-metadata-server-for-non-production-environments',
  'other_deviance-standard_sft-websphere-mq-for-non-production',
  'other_deviance-standard_sft-websphere-mq-queue-manager',
  'other_deviance-standard_sft-websphere-mq-telemetry',
  'other_deviance-standard_sft-wget',
  'other_deviance-standard_sft-windows-assessment-and-deployment-kit',
  'other_deviance-standard_sft-windows-azure-authoring-tools',
  'other_deviance-standard_sft-windows-azure-emulator',
  'other_deviance-standard_sft-windows-communication-foundation-wcf-ria-services',
  'other_deviance-standard_sft-windows-defender-advanced-threat-protection',
  'other_deviance-standard_sft-windows-media-player',
  'other_deviance-standard_sft-windows-performance-toolkit',
  'other_deviance-standard_sft-windows-phone-sdk',
  'other_deviance-standard_sft-windows-powershell',
  'other_deviance-standard_sft-windows-powershell-extensions-for-sql-server',
  'other_deviance-standard_sft-windows-resource-kit-tools',
  'other_deviance-standard_sft-windows-search',
  'other_deviance-standard_sft-windows-server',
  'other_deviance-standard_sft-windows-software-development-kit',
  'other_deviance-standard_sft-winlogbeat',
  'other_deviance-standard_sft-winmerge',
  'other_deviance-standard_sft-winmerge-portable',
  'other_deviance-standard_sft-winpcap',
  'other_deviance-standard_sft-winscp',
  'other_deviance-standard_sft-winzip',
  'other_deviance-standard_sft-wireshark',
  'other_deviance-standard_sft-wireshark-portable',
  'other_deviance-standard_sft-woodstox-core',
  'other_deviance-standard_sft-workflow-manager-tools-for-visual-studio',
  'other_deviance-standard_sft-workload-automation-agent',
  'other_deviance-standard_sft-workload-scheduler-dynamic-agent',
  'other_deviance-standard_sft-workload-scheduler-fault-tolerant-agent',
  'other_deviance-standard_sft-workspace-app',
  'other_deviance-standard_sft-workstation',
  'other_deviance-standard_sft-wpasupplicant',
  'other_deviance-standard_sft-xbean',
  'other_deviance-standard_sft-xdg-utils',
  'other_deviance-standard_sft-xerces-c',
  'other_deviance-standard_sft-xfsdump',
  'other_deviance-standard_sft-xfsprogs',
  'other_deviance-standard_sft-xinetd',
  'other_deviance-standard_sft-xl-c-for-aix',
  'other_deviance-standard_sft-xl-cc',
  'other_deviance-standard_sft-xl-cc-for-aix',
  'other_deviance-standard_sft-xml-commons-resolver',
  'other_deviance-standard_sft-xml-core-services',
  'other_deviance-standard_sft-xrdp',
  'other_deviance-standard_sft-xstream',
  'other_deviance-standard_sft-xz',
  'other_deviance-standard_sft-z-workload-scheduler-agent',
  'other_deviance-standard_sft-zabbix',
  'other_deviance-standard_sft-zarafa-client',
  'other_deviance-standard_sft-zenity',
  'other_deviance-standard_sft-zip',
  'other_deviance-standard_sft-zlib',
  'other_deviance-standard_sft-zulu-java-runtime-environment',
  'other_deviance-standard_shuttle-hardware',
  'other_deviance-standard_siemes-hardware',
  'other_deviance-standard_skiasharp-library',
  'other_deviance-standard_snakeyaml-library',
  'other_deviance-standard_sok8s',
  'other_deviance-standard_solaris',
  'other_deviance-standard_solaris-vm-tradi',
  'other_deviance-standard_solr-middleware',
  'other_deviance-standard_spark-azure',
  'other_deviance-standard_spark-library',
  'other_deviance-standard_spark3-middleware',
  'other_deviance-standard_specflow-library',
  'other_deviance-standard_spring-ai-library',
  'other_deviance-standard_spring-amqp-library',
  'other_deviance-standard_spring-batch-library',
  'other_deviance-standard_spring-boot-library',
  'other_deviance-standard_spring-cloud-library',
  'other_deviance-standard_spring-data-library',
  'other_deviance-standard_spring-framework-library',
  'other_deviance-standard_spring-graphql-library',
  'other_deviance-standard_spring-hateoas-library',
  'other_deviance-standard_spring-integration-library',
  'other_deviance-standard_spring-kafka-library',
  'other_deviance-standard_spring-ldap-library',
  'other_deviance-standard_spring-modulith-library',
  'other_deviance-standard_spring-plugin-library',
  'other_deviance-standard_spring-restdocs-library',
  'other_deviance-standard_spring-retry-library',
  'other_deviance-standard_spring-security-library',
  'other_deviance-standard_spring-security-rsa-library',
  'other_deviance-standard_spring-session-library',
  'other_deviance-standard_spring-shell-library',
  'other_deviance-standard_spring-statemachine-library',
  'other_deviance-standard_spring-vault-library',
  'other_deviance-standard_spring-webflow-library',
  'other_deviance-standard_spring-ws-library',
  'other_deviance-standard_springazure-library',
  'other_deviance-standard_springdoc-library',
  'other_deviance-standard_sql',
  'other_deviance-standard_sql-azure',
  'other_deviance-standard_sql-browser',
  'other_deviance-standard_sql-monitor',
  'other_deviance-standard_sql-server-analysis-services',
  'other_deviance-standard_sql-server-analysis-services-middleware',
  'other_deviance-standard_sql-server-integration-services',
  'other_deviance-standard_sql-server-integration-services-middleware',
  'other_deviance-standard_sql-server-reporting-services',
  'other_deviance-standard_sql-server-reporting-services-middleware',
  'other_deviance-standard_sterling-file-gateway',
  'other_deviance-standard_storybook-library',
  'other_deviance-standard_streams-middleware',
  'other_deviance-standard_struts2-library',
  'other_deviance-standard_subject7',
  'other_deviance-standard_success-data',
  'other_deviance-standard_sun-hardware',
  'other_deviance-standard_super-micro-hardware',
  'other_deviance-standard_swashbuckle-library',
  'other_deviance-standard_swc-library',
  'other_deviance-standard_sybase-sysam',
  'other_deviance-standard_synchronization-services-for-ado-dot-net',
  'other_deviance-standard_syrius-hardware',
  'other_deviance-standard_sysbase-ase',
  'other_deviance-standard_sysbase-dco',
  'other_deviance-standard_sysbase-iq',
  'other_deviance-standard_sysbase-replication-server',
  'other_deviance-standard_tableau',
  'other_deviance-standard_talend-bigdata',
  'other_deviance-standard_tanstack-query-library',
  'other_deviance-standard_tanstack-router-library',
  'other_deviance-standard_tanstack-store-library',
  'other_deviance-standard_teradata',
  'other_deviance-standard_teradata-hardware',
  'other_deviance-standard_thales-hardware',
  'other_deviance-standard_tibco-rdv-middleware',
  'other_deviance-standard_tibco-rdv-middleware-consumer',
  'other_deviance-standard_to-be-checked-band-hardware',
  'other_deviance-standard_tomcat',
  'other_deviance-standard_tomcat-library',
  'other_deviance-standard_traffic-manager-tm-service',
  'other_deviance-standard_traffic-manager-zone',
  'other_deviance-standard_typeform',
  'other_deviance-standard_typescript-library',
  'other_deviance-standard_ucopia-hardware',
  'other_deviance-standard_ui-for-asp-dot-net-mvc',
  'other_deviance-standard_unknown-middleware',
  'other_deviance-standard_vault',
  'other_deviance-standard_vision-for-dot-net',
  'other_deviance-standard_visual-basic-dot-net',
  'other_deviance-standard_visual-studio-ado-dot-net-entity-framework-tools',
  'other_deviance-standard_vite-library',
  'other_deviance-standard_vitest-library',
  'other_deviance-standard_vms',
  'other_deviance-standard_vms-vm-tradi',
  'other_deviance-standard_vmware-hardware',
  'other_deviance-standard_web-middleware',
  'other_deviance-standard_weblogic-portal-middleware',
  'other_deviance-standard_weblogic-server',
  'other_deviance-standard_webmethods',
  'other_deviance-standard_webpack-library',
  'other_deviance-standard_whole-tomato',
  'other_deviance-standard_wildfly-library',
  'other_deviance-standard_wildfly-operators-middleware',
  'other_deviance-standard_windows',
  'other_deviance-standard_windows-azure-libraries-for-dot-net',
  'other_deviance-standard_windows-vm-tradi',
  'other_deviance-standard_wordline',
  'other_deviance-standard_x2',
  'other_deviance-standard_xap-dot-net',
  'other_deviance-standard_xceed',
  'other_deviance-standard_xelerate',
  'other_deviance-standard_xenon-hardware',
  'other_deviance-standard_xstream-library',
  'other_deviance-standard_xunit-v2-library',
  'other_deviance-standard_xunit-v3-library',
  'other_deviance-standard_yarn-middleware',
  'other_deviance-standard_yarp-library',
  'other_deviance-standard_zos',
  'other_deviance-standard_zos-vm-tradi',
  'other_deviance-standard_zulu-java-development-kit-azul-systems',
] as const;

export const COST_RESULT_KEYS = [
  'tco_gbsu-cost_workforce',
  'tco_gbsu-cost_workforce-prev-year',
  'tco_gbsu-cost_workforce-forecast',
  'tco_gbsu-cost_non-workforce',
  'tco_gbsu-cost_non-workforce-forecast',
  'tco_gbsu-cost_non-workforce-prev-year',
  'tco_vendor-cost_software',
  'tco_vendor-cost_software-forecast',
  'tco_vendor-cost_software-prev-year',
  'tco_infrastructure-cost_gts',
  'tco_infrastructure-cost_gts-forecast',
  'tco_infrastructure-cost_gts-prev-year',
  'tco_infrastructure-cost_azure',
  'tco_infrastructure-cost_azure-forecast',
  'tco_infrastructure-cost_azure-prev-year',
  'tco_infrastructure-cost_aws',
  'tco_infrastructure-cost_aws-forecast',
  'tco_infrastructure-cost_aws-prev-year',
  'cost',
  'cost-prev-year',
  'cost-forecast',
] as const;

export const OBSOLESCENCE_PERCENTAGE_KEY = 'obsolescence-percentage';
export const OBSOLESCENCE_ASSET_SUM_KEY = 'obsolescence-assets-sum';

export const RESULT_KEYS = [
  ...COMPONENT_RESULT_KEYS,
  ...SCOPE_RESULT_KEYS,
  ...PARENT_RESULT_KEYS,
  OBSOLESCENCE_PERCENTAGE_KEY,
  OBSOLESCENCE_ASSET_SUM_KEY,
] as const;

export type ComponentResultKey = (typeof COMPONENT_RESULT_KEYS)[number];
export type ScopeResultKey = (typeof SCOPE_RESULT_KEYS)[number];
export type ParentResultKey = (typeof PARENT_RESULT_KEYS)[number];
export type ResultKey = (typeof RESULT_KEYS)[number];
export type CostResultKey = (typeof COST_RESULT_KEYS)[number];

export type IApplicationFlat = {
  applicationId: number;
  iAppliID: string;
  iAppliName: string;
  recordDate: Date;
  createdDate: Date;
  description: string;
} & { [key in ComponentResultKey | ScopeResultKey]: string };

export interface INotifications {
  applicationId: number;
  roadmapsOutdated: number;
  claimsRequested: number;
  recommendation: number;
}

export type NotificationType = 'claims' | 'roadmaps' | 'recommendation' | 'exception' | 'roadmapsOutdated';

export type IInformationFormat = {
  key: string;
  children: IInformationFormat[];
};

export interface ICertified {
  applicationId: string;
  isCertified: boolean;
}

export enum ResultStatus {
  CERTIFIED = 'certified',
  ERROR = 'error',
  IDLE = 'idle',
}

export type MetassetSonar = {
  bugs: {
    count: number;
    rating: string;
  };
  vulnerabilities: {
    count: number;
    rating: string;
  };
  hotspotsReviewed: {
    count: number;
    rating: string;
  };
  codeSmell: {
    count: number;
    rating: string;
  };
  coverage: {
    percentage: number;
    lineCoverage: number;
  };
  duplications: {
    duplicatedLines: number;
    duplicatedLinesDensity: number;
  };
  ncloc: number;
  languageList: Array<string>;
  lastAnalysisDate: string;
  assetUrl: string;
  assetId: string;
};

export type TrafficManagerMember = {
  ip: string;
  name: string;
  priority: number;
  region: string;
  availabilityZone: string;
  uuid: string;
  enabled: boolean;
};

export interface IMetadata {
  previousValueCmp?: 'decrease' | 'equal' | 'increase' | null;
  ruleVersion?: string;
  ruleLink?: string;
  ruleKey?: string;
  name?: string;
  isCost?: boolean;
  importMonth?: string;
  isDuplicatedApptioResult?: boolean;
  activeSupport?: string;
  activeSupportSource?: string;
  activeSupportLastUpdateBy?: string;
  activeSupportLastUpdateDate?: string;
  securitySupport?: string;
  securitySupportSource?: string;
  securitySupportLastUpdateBy?: string;
  securitySupportLastUpdateDate?: string;
  extendedSupport?: string;
  extendedSupportSource?: string;
  extendedSupportLastUpdateBy?: string;
  extendedSupportLastUpdateDate?: string;
  lifeCycleManagement?: string;
  lifeCycleManagementSource?: string;
  lifeCycleManagementLastUpdateBy?: string;
  lifeCycleManagementLastUpdateDate?: string;
  eolScore?: string;
  link?: string;
  host?: string; // hostname of the dali asset
  zone?: string; // zone of the dali asset
  uid?: string; // uid of the dali asset
  serverVersion?: string; // os version for transformation path assets
  serverOs?: string; // os name for transformation path assets
  members?: Array<TrafficManagerMember>;
  obsolescence?: {
    totalInstanceCount: number;
    obsoleteInstanceCount: number;
    obsolescenceDenominator: number;
    percentageObsolete: number;
  };
  persistentResult?: PersistentResultMetata;
  serverCloudType?: string;
  daliGoldenSource?: string;
  isActive?: boolean;
  monitoring?: Record<string, unknown>[] | null;
  webAppUrls?: string;
  /**
   * Extra information about a result
   */
  sonar?: MetassetSonar;
  data?: {
    [key: string]: string;
  };
  dataArray?: {
    [key: string]: Array<Record<string, string>>;
  };
  transformationAction?: NorthStarTarget;
  transformationDate?: string;
  transformationStartDate?: string;
  transformationEndDate?: string;
  transformationProgress?: NorthStarTransfoProgress;
  austinStartDate?: string;
  austinEndDate?: string;
  northStarTarget?: NorthStarTarget;
  itResourceSubTower?: string;
  itResourceTower?: string;
  cve?: string; // JFrog SCA
  fixedVersion?: string; // JFrog SCA
  impactPath?: string; // JFrog SCA
  path?: string; // JFrog SCA
  saving?: string | number;
}

export enum PersistentResultType {
  ASSET = 'asset',
  VULNERABILITY = 'vulnerability',
  FINOPS = 'finops',
}

export type PersistentResultMetata =
  | ({
      assetHostname: string;
      assetLink: string;
      assetName: string;
      assetServerCloudType: string;
      assetUid: string;
      assetZone: string;
    } & {
      type: PersistentResultType.ASSET;
    })
  | ({
      vulnerabilityCve: string;
      vulnerabilityDeadline: string;
      vulnerabilityDescription: string;
      vulnerabilityFixedVersion: string;
      vulnerabilityImpactedArtifact: string;
      vulnerabilityImpactPath: string;
      vulnerabilityPath: string;
      vulnerabilityProjectKey: string;
      vulnerabilityVulnerableComponent: string;
    } & {
      type: PersistentResultType.VULNERABILITY;
    })
  | ({ resourceLink: string; resourceName: string } & {
      type: PersistentResultType.FINOPS;
    });

export interface IVersion {
  id: number;
  version: string;
  status: string;
  description: string | null;
}

export type IdType = 'iAppliID' | 'kearID';

export interface IApplicationFullResult {
  createdDate: Date;
  applicationId: number;
  status: ResultStatus;
  key: string;
  type: ApplicationResultType;
  resultId: number;
  name: string;
  parentRefId: number | null;
  scopeRefId: number | null;
  componentRefId: number | null;
  parentId: number | null;
  scopeId: number | null;
  componentId: number | null;
  valueMutated: string;
  commentMutated: string;
  sourceIds: number[];
  sources: Array<{ name: string; link: string | null; comment: string | null }>;
  sourceIdsMutated: number[];
  recommendationIds: number[];
  confidenceId: number;
  confidenceMutated: {
    value: number;
    comment: string;
  };
  versionRefId: number | null;
  version: Pick<IApplicationVersionRef, 'description' | 'id' | 'status' | 'version' | 'advisedVersion'>;
  isMainResult: boolean;
  value: string;
  comment: string;
  good?: string;
  medium?: string;
  bad?: string;
  metadata: IMetadata | null;
  rule: ScopeResult;
}

export type IApplicationFull = {
  applicationId: number;
  global_taxon_iappliid: IApplicationFullResult[];
  global_taxon_iappliname: IApplicationFullResult[];
  global_taxon_kearid: IApplicationFullResult[];
  recordDate: string;
  global_taxon_description: IApplicationFullResult[];
  ownerIds: number[];
  tagIds: number[];
  claimIds: number[];
  exceptionIds: number[];
  recommendationIdsRecorded: number[];
  roadmapIdsRecorded: number[];
  roadmapIds: number[];
} & { [key in ResultKey]: IApplicationFullResult[] } & {
  [key in keyof IBaseEntity]: IBaseEntity[key];
};

export interface IApplicationsFullPayload {
  items: IApplicationFull[];
  meta: IPagination;
  links: {
    first: string;
    previous: string;
    next: string;
    last: string;
  };
}

export interface IHeader {
  resultKey: ResultKey | string;
  label: string | null;
  children?: IHeader[];
}
export interface IApplicationsFullResult {
  recordDate: Date;
  applicationId: number;
  [index: number]: IApplicationFullResult;
}

export type PreviousValueCmpType = 'equal' | 'increase' | 'decrease';

export interface IGroupedApplications {
  applicationName: string;
  results: IApplicationFull[];
}

export interface IGroupedApplicationsPaginate {
  applicationsFull: IApplicationFull[];
  totalPages: number;
  currentPage: number;
  totalItems: number;
}

export interface IUniqueVisitors {
  linePoints: IUniqueVisitorsInformation[];
  timeBucketApplied: string;
}

export interface IUniqueVisitorsInformation {
  uniqueVisitorsNumber: number;
  intervalStartingDate: Date;
}

export interface IEntityAggrByDate {
  date: string;
  count: number;
}

export interface ICountEvolution {
  label: string;
  results: IEntityAggrByDate[];
}
export interface IKpiKeys {
  applicationsCount: number;
  favoritesCount: number;
  recommendationsCount: number;
  exceptionsCount: number;
  roadmapsCount: number;
  certificationsCount: number;
  errorsCount: number;
}
export interface ICountKpi {
  date: string;
  counts: IKpiKeys;
}

export interface IGroupedTotalCostResult {
  groupedType: string;
  children: string[];
}

export interface ITotalCostResults {
  results: Partial<IApplicationResult>[];
  label: string;
}
export interface IHighchartsEvolution {
  date: string;
  value: number | null;
}
export interface IHighchartData {
  name: string;
  key?: string;
  data: IHighchartsEvolution[];
  stack: string;
  color: string;
}

export type ITotalRecord = {
  recordDate: string;
} & { [key in CostResultKey]: string };

export interface IMenuItem<T> {
  label: string;
  view: T;
  display: boolean;
  children?: IMenuItem<T>[];
}
export type SettingMenu = Array<IMenuItem<SETTINGS_VIEW>>;
export type DashboardMenu = Array<IMenuItem<DASHBOARD_VIEW>>;

export interface IChasing extends IBaseEntity {
  applicationId: number;
  chasingRefId: number | null;
  roadmapId: number | null;
  componentStatusId: number | null;
  resultId: number | null;
  action: string;
  result: IApplicationResult | null;
  roadmap: IRoadmap;
  chasingRef?: IChasingRef;
  recommendation: IRecommendationNext;
}

export interface IChasingRef extends IBaseEntity {
  recordFrom: string;
  recordTo: string;
  resultKey: string | null;
  type: string;
  targetedResultValue: string | null;
  targetedMode: string | null;
  chasings: IChasing[];
  roadmaps: IRoadmap[];
  recommendations: IRecommendationNext[];
  applicationId: null | number;
}

export type ChasingType = 'component-status' | 'roadmap' | 'result' | 'recommendation' | 'application';

export interface IActionPayload {
  action: 'send-mail' | 'none';
  mailBody: string;
  applicationIds?: number[];
}
export interface IApplicationsLanguages {
  name: string;
  percentage: number;
  repositories?: Array<{ name: string; link: string }>;
}

export type ScopeResult = 'good' | 'medium' | 'bad' | 'n/a' | 'n/d' | 'intermediate';

export interface IRule extends IBaseEntity {
  scopeKey: string;
  version: string;
  description: string;
  good: string | null;
  medium: string | null;
  bad: string | null;
}

export interface IHistory extends IBaseEntity {
  event: string;
  additionalInfo: string;
}

export type HistoryPaginated = Paginated<IHistory>;
export type ChasingRefPaginated = Paginated<IChasingRef>;
export type ResultPaginated = Paginated<IApplicationResult>;

export type HistoryEntity = string;
export type HistoryAction = 'creation' | 'deletion' | 'addition' | 'update';

export type HistoryEvent = `${HistoryEntity}.${HistoryAction}`;

export interface IRoadmapAncestors extends IRoadmap {
  parentRef?: IApplicationParentRef;
  scopeRef?: IApplicationScopeRef[];
}

export interface IRecordResult {
  good: number | null;
  bad: number | null;
  medium: number | null;
  noData: number | null;
  notApplicable: number | null;
  value: number | null;
  recordDate: string;
}

export interface IRecordResultsByKey {
  resultKey: string;
  results: IRecordResult[];
}

export interface ITotalCount {
  total: number;
}

export type ReducerActionMap<M extends Record<string, unknown>> = {
  [Key in keyof M]: M[Key] extends undefined
    ? {
        type: Key;
      }
    : {
        type: Key;
        payload: M[Key];
      };
};

export type ComponentRefFromApi = {
  componentRefId: number;
  componentRefName: string;
  componentRefType: string | null;
  scopeRefId: number;
  scopeRefName: string;
};

export interface RecommandationWithRemediation {
  applicationId: number;
  applicationName: string;
  applicationTribe: string;
  applicationCriticality: string;
  applicationStatus: string;
  parentRefId: number;
  scopeRefId: number;
  scopeRefName: number;
  componentRefId: number | null;
  componentRefKey: string;
  componentRefName: string;
  componentRefType: string;
  versionRefId: number;
  version: string;
  recommendationId: string;
  recommendationCreatedDate: string;
  isManagedByExtendedTransactis: boolean;
  recommendationTitle: string;
  recommendationComment: string;
  recommendationDocumentationLink: string | null;
  recommendationActualValue: string | null;
  recommendationExpectedValue: string | null;
  recommendationStatus: RecommendationStatus;
  recommendationCriticity: string;
  recommendationType: string;
  roadmapExpectedDate: string | null;
  recommendationDueDate: string | null;
  roadmapId: number | null;
  roadmapComment: string | null;
  roadmapStatus: string | null;
  roadmapType: string | null;
  roadmapAssets: PersistentResultDTO[] | null;
  roadmapAssetNames: Array<string>;
  roadmapAssetZones: Array<string>;
  exceptionComment: string | null;
  reportingSort: string | null;
  exceptionRafId: string | null;
  exceptionAssets: string | null;
  exceptionAssetNames: string | null;
  exceptionAssetZones: string | null;
  resultType: string | null;
  noMainResultValues: { name: string | null; value: string | null }[] | null;
  kearID: string;
  ownersEmail: string[];
  applicationRegulations: string[];
}

export interface Inventory {
  id: string;
  lastRefreshDate: Date;
  applicationId: number;
  resultId: number;
  resultValue: string;
  applicationTribe: string | null;
  applicationTribeLevel1: string | null;
  applicationTribeLevel2: string | null;
  applicationTribeLevel3: string | null;
  iAppliID: string | null;
  kearID: string | null;
  iAppliName: string | null;
  ownersEmail: Array<string> | null;
  applicationTrigram: string | null;
  applicationCriticality: string | null;
  applicationType: string | null;
  applicationCountry: string | null;
  applicationIsAmerManaged: string | null;
  applicationManagedBy: string | null;
  applicationOwnedBy: string | null;
  applicationStatus: string | null;
  applicationOwnershipRc: string | null;
  componentRefId: number | null;
  componentRefKey: string | null;
  componentRefType: string | null;
  componentRefName: string | null;
  scopeRefName: string | null;
  versionRefId: number | null;
  version: string | null;
  advisedVersion: string | null;
  assetServerCloudType: string | null;
  assetNumberOfCores: string | null;
  assetInstalledMemory: string | null;
  assetNumberOfLogicalCpu: string | null;
  assetHostname: string | null;
  assetZone: string | null;
  assetName: string | null;
  assetArchitectureType: string | null;
  assetUid: string | null;
  assetServerStatus: string | null;
  assetServerEnvironment: string | null;
  assetServerBuilding: string | null;
  assetServerRoom: string | null;
  assetServerSite: string | null;
  assetServerCountry: string | null;
  assetServerExposed: string | null;
  assetServerServiceOffer: string | null;
  assetServerMainIpAddress: string | null;
  assetServerVrfName: string | null;
  assetServerBrand: string | null;
  eolSupport: string | null;
  eolSupportDateSource: string | null;
  eolSupportDateLastUpdateBy: string | null;
  eolSupportDateLastUpdateDate: Date | null;
  eolActive: string | null;
  eolSecurity: string | null;
  eolExtended: string | null;
  lifeCycleManagement: string | null;
  exceptionComment: string | null;
  roadmapId: number | null;
  roadmapId2: number | null;
  roadmapExpectedDate: Date | null;
  roadmapExpectedDate2: Date | null;
  roadmapComment: string | null;
  roadmapComment2: string | null;
  roadmapStartDate: Date | null;
  roadmapStartDate2: Date | null;
  roadmapActions: Pick<RoadmapAction, 'comment' | 'date' | 'type'>[] | null;
  itrmObsolescenceStatus: string;
  recommendationId: number | null;
  recommendationTitle: string;
  recommendationStatus: string | null;
  recommendationType: string | null;
  recommendationId2: number | null;
  recommendationTitle2: string;
  recommendationStatus2: string | null;
  recommendationType2: string | null;
  isManagedByExtendedTransactis: boolean | null;
  isCBS: boolean;
  isOutOfScope: boolean;
  eolYear: string | null;
  isManagedByGTS: boolean;
  eolDate: string | null;
  roadmapExpectedDateWithoutTimeZone: string | null;
  roadmapExpectedDateWithoutTimeZone2: string | null;
  assetTransformationPath: string | null;
  assetTransformationTarget: string | null;
  assetTransformationDate: Date | null;
  applicationTransformationPath: string | null;
  applicationTransformationTarget: string | null;
  applicationTransformationDate: string | null;
  applicationAustinStartDate: string | null;
  applicationAustinEndDate: string | null;
  assetServerVlanId: string | null;
  assetServerSubnetName: string | null;
  applicationRegulations: string[] | null;
}

export type GroupedFilters = Array<Record<string, string | null>>;
export type FilterPossibleValues = GroupedFilters | string[];

export interface RecommandationWithRemediationFiltersValues {
  filters: Record<keyof RecommandationWithRemediation | 'scopes', FilterPossibleValues>;
}

export interface InventoryFiltersValues {
  filters: Record<keyof Inventory, FilterPossibleValues>;
}

export enum WebAppEnvironment {
  PRODUCTION = 'production',
  DEVELOPMENT = 'development',
}

export interface DrawGraphNodeData {
  id?: string;
  label?: string;
  color?: string;
  source?: string;
  target?: string;
  arrow?: string;
  type?: string;
  key?: string;
  countLink?: number;
  links?: string;
  level?: number;
}

export interface WebAppNode {
  data: DrawGraphNodeData;
  position?: string;
}

export * from './bookmark.type';
export * from './communications.type';
export * from './deviance.type';
export * from './excel.type';
export * from './exception.type';
export * from './filter.type';
export * from './kpi.type';
export * from './my-scope.type';
export * from './nova.type';
export * from './obsolescence.type';
export * from './persistent-result.type';
export * from './recommendation-next.type';
export * from './remediations.type';
export * from './roadmap.type';
export * from './thirdpartySoftware.type';
export * from './total.type';
export * from './user.type';
export * from './versionRef.type';
